import { ConnectionState, createLocalAudioTrack, createLocalVideoTrack, MediaDeviceFailure, RoomEvent, Track, TrackEvent, VideoPresets, } from 'livekit-client';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { getDevices, pushCallDebugLog, randomString, } from 'vcs-common';
import { createActiveSpeakerRegistry } from './activeSpeakers';
import { applyInitialVideoState, finalizeInitialVideoState } from './applyInitialVideoState';
import { createAudioHealthLogger } from './audioHealthLog';
import { planIdentityConnect } from './connectionIdentity';
import { enableMicrophone, MICROPHONE_CAPTURE_OPTIONS, republishMicrophone } from './localAudioControls';
import { enqueueMediaToggle, switchLocalVideoDevice, toggleLocalVideoTrack } from './localVideoControls';
import { createRemoteAudioPlayback } from './remoteAudioPlayback';
import { attachCurrentParticipantTracks, detachCurrentParticipantTracks } from './remoteTrackAttachment';
import { getRemoteVideoMuted, setRemoteVideoMuted, subscribeToRemoteVideoState } from './remoteVideoState';
import { useLiveKitChat } from './data-channels/chat/useLiveKitChat';
import { MEET_CHAT_IMAGE_TOPIC } from './data-channels/chat/chatDataChannelUtils';
import { useLiveKitCloudsDataChannel } from './data-channels/clouds/useLiveKitCloudsDataChannel';
import { resolvePreferredDevices } from './core/media/mediaDevicePreferences';
import { useLiveKitVirtualBackground } from './core/media/useLiveKitVirtualBackground';
import { useScreenShare } from './core/media/useScreenShare';
import { PeerMetricsManager } from './core/metrics/peerMetricsManager';
import { useLiveKitRtcStats } from './core/metrics/useLiveKitRtcStats';
import { useVideoQualityMonitor } from './core/metrics/useVideoQualityMonitor';
import { isJoined, shouldSubscribeToRemote } from './core/room/participantMetadata';
import { createLiveKitRoom, getLiveKitServerHost } from './core/room/liveKitRoomOptions';
import { useRoomDataTransport } from './core/transport/useRoomDataTransport';
/** 1:1 публикует один слой, поэтому захват сразу ограничен целевым профилем. */
const ONE_TO_ONE_VIDEO_CAPTURE = {
    resolution: VideoPresets.h540.resolution,
    frameRate: 24,
};
/** Сколько ждём ack от клиента на request_mute/request_stop_video, прежде чем считать команду неподтверждённой. */
const MODERATION_ACK_TIMEOUT_MS = 5000;
/**
 * [psy-wait] ВРЕМЕННАЯ ДИАГНОСТИКА: срез участника для разбора «клиент ждёт психолога,
 * хотя психолог вошёл». Удалить вместе с остальными пробами `psy-wait`.
 */
function describeParticipantForProbe(participant) {
    if (!participant)
        return null;
    return {
        identity: participant.identity,
        name: participant.name,
        metadata: participant.metadata,
        tracks: participant.trackPublications.size,
        isJoined: isJoined(participant),
        looksLikePsychologist: participant.name?.split('-').at(-1)?.trim() === 'psychologist',
    };
}
/**
 * [psy-wait] ВРЕМЕННАЯ ДИАГНОСТИКА: гранты выданного токена. Асимметрия видимости
 * (психолог видит клиента, клиент психолога — нет) объясняется либо `hidden`,
 * либо урезанным `canSubscribe`, и то и другое лежит в payload JWT.
 */
function describeTokenGrantsForProbe(token) {
    try {
        const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
        return {
            identity: payload?.sub,
            room: payload?.video?.room,
            hidden: payload?.video?.hidden,
            canSubscribe: payload?.video?.canSubscribe,
            canPublish: payload?.video?.canPublish,
        };
    }
    catch (error) {
        return { parseError: String(error) };
    }
}
export function useLiveKitVideoRoom(roomCode, generateToken, onStatsReport, onSupervisorResponse, setSupervisorSend, onRecordingStatusUpdate, onCloudsOpen, onCloudsClose, onCloudsDrawLine, onCloudsClearDrawing, onCloudsSelectImage, onCloudsClientFinished, onCloudsUndo, onCloudsHideVideoForClient, onMacCardsOpen, 
/** Групповая терапия, МАК: психолог получил выбор карт от клиента по data channel */
onClientMacSelection, 
/** Групповая терапия, МАК: психолог закрыл инструмент — клиент тоже закрывает по data channel */
onMacCardsClose, 
/** Групповая терапия, Детектор эмоций: психолог открыл инструмент — клиент показывает выбор эмоций */
onEmotionsOpen, 
/** Групповая терапия, Детектор эмоций: психолог получил выбор эмоций от клиента по data channel */
onClientEmotionsSelection, 
/** Групповая терапия, Детектор эмоций: психолог закрыл инструмент — клиент тоже закрывает по data channel */
onEmotionsClose, 
/** Групповая терапия: клиент запросил подключение (ожидает разрешения психолога) */
onGroupConnectionRequest, 
/** Групповая терапия: психолог разрешил подключение — клиент получает и включает медиа */
onConnectionAllowed, 
/** Групповая терапия: психолог выключил микрофон участнику — обновить UI (иконка перечёркнута) */
onMutedByHost, 
/** Групповая терапия: психолог остановил видео участнику — синхронизировать состояние кнопки камеры */
onVideoStoppedByHost, 
/** Групповая терапия: психолог удалил участника — клиент получает и должен перейти на экран «Сессия завершена» */
onRemovedFromMeeting, 
/** Групповая терапия: психолог назначил текущего участника соорганизатором */
onCoOrganizerAssigned, 
/** Групповая терапия: любой участник (в т.ч. ещё не допущенный) отключился — очередь ожидания убирает его сама */
onRemoteParticipantLeft, 
/** Групповая терапия, МАК: психолог транслирует карты участника на «общий экран» (клиент получает) */
onMacSharedView, 
/** Групповая терапия, Детектор эмоций: психолог транслирует эмоции участника на «общий экран» (клиент получает) */
onEmotionsSharedView, 
/** Песочная терапия: психолог открыл инструмент — клиент получает и отображает песочницу */
onSandTherapyOpen, 
/** Песочная терапия: психолог закрыл инструмент — клиент тоже закрывает песочницу */
onSandTherapyClose, 
/** Песочная терапия: клиент разместил фигуру на поле — психолог получает данные фигуры с позицией и поворотом */
onSandPlaceFigure, 
/** Песочная терапия: клиент переместил фигуру — психолог получает новые координаты */
onSandMoveFigure, 
/** Песочная терапия: клиент повернул фигуру — психолог получает id фигуры для синхронизации поворота */
onSandRotateFigure, 
/** Песочная терапия: клиент изменил размер фигуры — психолог получает id фигуры и новый масштаб */
onSandResizeFigure, 
/** Песочная терапия: клиент удалил фигуру с поля — психолог удаляет её из своего состояния */
onSandRemoveFigure, 
/** Песочная терапия: психолог изменил цвет песка — клиент применяет новый цвет фона песочницы */
onSandColorChange, 
/** Песочная терапия: клиент подтвердил готовность — психолог получает сигнал о загрузке песочницы у клиента */
onSandClientReady, 
/** Песочная терапия: психолог выбрал набор фигур для сессии — клиент получает список доступных фигур */
onSandSelectFigures, 
/** Песочная терапия: психолог запустил этап расстановки — клиент переходит в режим размещения фигур */
onSandStartPlacement, 
/** Базовый чат встречи по data channel */
onChatMessage, 
/** Color code */
onColorCodeOpen, onColorCodeClose, onColorCodeResult, 
/** Wheel */
onWheelOpen, onWheelChange, onWheelResult, onWheelClose, 
/** DPDG */
onDpdgOpen, onDpdgClose, onDpdgMessage, onDpdgVideoControl, options) {
    const [joined, setJoined] = useState(false);
    const livekitServerUrl = options?.livekitServerUrl ?? `${import.meta.env.VITE_REACT_APP_LIVEKIT_URL}`;
    const livekitServerHost = useMemo(() => getLiveKitServerHost(livekitServerUrl), [livekitServerUrl]);
    const isGroupConsultation = options?.isGroupConsultation ?? false;
    const [room, setRoom] = useState(() => createLiveKitRoom(isGroupConsultation));
    const roomRef = useRef(room);
    roomRef.current = room;
    const leavePromiseRef = useRef(null);
    const remoteSyncTimerRef = useRef(null);
    const reconnectToastId = useRef(null);
    const [connected, setConnected] = useState(null);
    const [destroyed, setDestroyed] = useState(false);
    const [localAudio, setLocalAudio] = useState(null);
    const [localAudioOutput, setLocalAudioOutput] = useState(null);
    const [localVideo, setLocalVideo] = useState(null);
    const [remoteStreams, setRemoteStreams] = useState([]);
    const [localVideoStream, setLocalVideoStream] = useState();
    const [localVideoTrack, setLocalVideoTrack] = useState(null);
    const [isAudioMuted, setIsAudioMuted] = useState(false);
    const [isVideoMuted, setIsVideoMuted] = useState(false);
    const [isLocalActiveSpeaker, setIsLocalActiveSpeaker] = useState(false);
    /** Микрофон включён, но звук от него не уходит: дорожка не опубликована, молчит или не шлёт данные. */
    const [isMicrophoneUnheard, setIsMicrophoneUnheard] = useState(false);
    const isMicrophoneFailed = useRef(false);
    const isCameraFailed = useRef(false);
    const isAudioFailed = useRef(false);
    const remoteStreamsMap = useRef(new Map());
    const activeSpeakersRef = useRef(createActiveSpeakerRegistry());
    const isJoinedRef = useRef(false);
    const joiningRef = useRef(false);
    /**
     * Мьют/стоп-видео участника по требованию модератора — это «просьба» клиенту
     * по data-channel, а не серверное принуждение (LiveKit Server API недоступен
     * из браузера). Ждём ack от клиента, который реально выполнил mute, и по
     * таймауту считаем команду неподтверждённой.
     */
    const pendingModerationAcksRef = useRef(new Map());
    const audioOutputDeviceIdRef = useRef();
    const reconnectToastDelayRef = useRef(null);
    const localAudioTrackRef = useRef(null);
    const localVideoTrackRef = useRef(null);
    const videoToggleQueueRef = useRef(Promise.resolve(false));
    const audioToggleQueueRef = useRef(Promise.resolve(false));
    /** Состояние камеры, выбранное в лобби: применяется к треку, который создаст joinRoom. */
    const initialCameraEnabledRef = useRef(true);
    const connectQueueRef = useRef(Promise.resolve());
    const preconnectRequestedRef = useRef(false);
    const remoteVideoStateCleanupRef = useRef(new Map());
    const remoteAudioPlaybackRef = useRef(null);
    const reconcileRemoteParticipantsRef = useRef(() => undefined);
    const statsTimerRef = useRef();
    const peerMetricsManagerRef = useRef(null);
    const audioHealthLoggerRef = useRef(createAudioHealthLogger());
    const { isScreenSharing, startScreenShare, stopScreenShare, cleanup: cleanupScreenShare } = useScreenShare(room);
    const connectionQuality = useVideoQualityMonitor({
        room,
        isGroupConsultation,
        isScreenSharing,
        localVideoTrackRef,
    });
    const { dataTransport, notify } = useRoomDataTransport(room);
    const { handleChatData, sendChatMessage } = useLiveKitChat({
        room,
        onChatMessage,
    });
    const { backgroundMode, backgroundModeRef, isBackgroundSupported, disableBackground, toggleBackground } = useLiveKitVirtualBackground({
        localVideoTrackRef,
        localVideoTrack,
        isVideoMuted,
    });
    const publishReliableData = useCallback((message) => {
        if (!message || typeof message !== 'object' || !('type' in message))
            return;
        dataTransport.publish(message);
    }, [dataTransport]);
    const { handleCloudsData, sendCloudsOpen, sendCloudsClose, sendCloudsDrawLine, sendCloudsClearDrawing, sendCloudsSelectImage, sendCloudsClientFinished, sendCloudsUndo, sendCloudsHideVideoForClient, } = useLiveKitCloudsDataChannel({
        room,
        publishReliableData,
        onCloudsOpen,
        onCloudsClose,
        onCloudsDrawLine,
        onCloudsClearDrawing,
        onCloudsSelectImage,
        onCloudsClientFinished,
        onCloudsUndo,
        onCloudsHideVideoForClient,
    });
    const connectToRoom = useCallback(async (userName) => {
        if (leavePromiseRef.current) {
            await leavePromiseRef.current;
        }
        const activeRoom = roomRef.current;
        // After leaveRoom disconnects, React `connected` may lag; trust LiveKit room state.
        if (activeRoom.state === ConnectionState.Connecting) {
            console.debug('Room connection already initialized', activeRoom.state);
            return;
        }
        const plan = planIdentityConnect({
            state: activeRoom.state,
            currentIdentity: activeRoom.localParticipant.identity,
            desiredIdentity: userName,
            isJoined: isJoinedRef.current,
        });
        // [psy-wait] ВРЕМЕННАЯ ДИАГНОСТИКА: что решили делать с identity и из какого состояния.
        console.info('[psy-wait] connectToRoom', {
            requested: userName,
            plan,
            roomState: activeRoom.state,
            currentIdentity: activeRoom.localParticipant.identity,
            isJoined: isJoinedRef.current,
        });
        pushCallDebugLog('psy-wait', `connectToRoom: ${plan} → ${userName}`, {
            plan,
            roomState: activeRoom.state,
            currentIdentity: activeRoom.localParticipant.identity,
        });
        if (plan === 'skip') {
            console.debug('Room connection already initialized', activeRoom.state);
            return;
        }
        if (plan === 'reconnect') {
            console.debug(`Reconnecting as ${userName}, was ${activeRoom.localParticipant.identity}`);
            pushCallDebugLog('reconnect', `Смена identity: ${activeRoom.localParticipant.identity} → ${userName}`, {
                from: activeRoom.localParticipant.identity,
                to: userName,
            });
            // Штатный обработчик Disconnected гасит таймер статистики и останавливает локальные
            // треки. При намеренной смене identity это лишнее — connectToRoom навесит его заново.
            activeRoom.removeAllListeners(RoomEvent.Disconnected);
            await activeRoom.disconnect(false);
        }
        const tokenRequest = {
            code: roomCode,
            is_echo_test: false,
            room_name: roomCode,
            server_type: 'livekit',
            user_identity: userName,
        };
        const tokenResult = await generateToken(tokenRequest);
        // [psy-wait] ВРЕМЕННАЯ ДИАГНОСТИКА: выдан ли токен под запрошенный identity.
        console.info('[psy-wait] токен получен', {
            requested: userName,
            hasToken: Boolean(tokenResult?.token),
            tokenLength: tokenResult?.token?.length ?? 0,
        });
        pushCallDebugLog('psy-wait', `Токен для ${userName}: ${tokenResult?.token ? 'выдан' : 'ПУСТО'}`);
        if (!tokenResult?.token) {
            console.error(`Empty token result for room ${roomCode}, user ${userName}`);
            // [psy-wait] ВРЕМЕННАЯ ДИАГНОСТИКА: пустой токен рвёт соединение и гасит опрос состава.
            console.error('[psy-wait] ПУСТОЙ ТОКЕН — соединение не поднимется', { requested: userName, roomCode });
            pushCallDebugLog('psy-wait', 'Пустой токен', { requested: userName }, 'error');
            setDestroyed(true);
            setConnected(false);
            if (localAudioTrackRef.current) {
                localAudioTrackRef.current.stop();
            }
            if (localVideoTrackRef.current) {
                localVideoTrackRef.current.stop();
                setLocalVideoTrack(null);
            }
            return;
        }
        // [psy-wait] ВРЕМЕННАЯ ДИАГНОСТИКА: длительность connect() отличает отказ сразу
        // от таймаута peer connection (~15 c), при котором сервер успевает показать
        // участника остальным, а сам участник так и не поднимается.
        const connectStartedAt = Date.now();
        try {
            console.info('[psy-wait] connect() старт', { requested: userName });
            pushCallDebugLog('psy-wait', `connect() старт → ${userName}`);
            await activeRoom.connect(livekitServerUrl, tokenResult.token, {
                autoSubscribe: false,
            });
            setConnected(true);
            // [psy-wait] ВРЕМЕННАЯ ДИАГНОСТИКА: что именно комната сообщает участнику
            // до входа — состав, события подключения и гранты собственного токена.
            pushCallDebugLog('psy-wait', `Подключились за ${Date.now() - connectStartedAt} мс`, {
                identity: activeRoom.localParticipant.identity,
                grants: describeTokenGrantsForProbe(tokenResult.token),
                remotesAtConnect: Array.from(activeRoom.remoteParticipants?.values() ?? []).map((p) => p.identity),
            });
            activeRoom.removeAllListeners(RoomEvent.ConnectionStateChanged);
            activeRoom.on(RoomEvent.ConnectionStateChanged, (state) => {
                pushCallDebugLog('psy-wait', `Состояние соединения: ${state}`);
            });
            // Слушатели лобби: молчат после входа, чтобы не дублировать логи сессии.
            activeRoom.on(RoomEvent.ParticipantConnected, (participant) => {
                if (isJoinedRef.current)
                    return;
                pushCallDebugLog('psy-wait', `Событие: подключился ${participant.identity}`, {
                    name: participant.name,
                });
            });
            activeRoom.on(RoomEvent.ParticipantDisconnected, (participant) => {
                if (isJoinedRef.current)
                    return;
                pushCallDebugLog('psy-wait', `Событие: отключился ${participant.identity}`);
            });
            activeRoom.removeAllListeners(RoomEvent.Reconnecting);
            activeRoom.removeAllListeners(RoomEvent.Reconnected);
            activeRoom.removeAllListeners(RoomEvent.Disconnected);
            activeRoom.on(RoomEvent.Reconnecting, () => {
                if (reconnectToastId.current !== null) {
                    toast.dismiss(reconnectToastId.current);
                }
                reconnectToastId.current = toast.warning('Связь нестабильна. Переподключаемся…', {
                    autoClose: false,
                    closeOnClick: false,
                });
            });
            activeRoom.on(RoomEvent.Reconnected, () => {
                setConnected(true);
                reconcileRemoteParticipantsRef.current();
                if (reconnectToastId.current !== null) {
                    toast.dismiss(reconnectToastId.current);
                    reconnectToastId.current = null;
                }
                toast.success('Соединение восстановлено');
                // Реконнект сбрасывает метаданные участника: без пере-публикации
                // собеседник считает нас невошедшим и прячет плитку.
                if (isJoinedRef.current) {
                    void (async () => {
                        if (activeRoom.state !== ConnectionState.Connected)
                            return;
                        const meta = { joined: true };
                        try {
                            await activeRoom.localParticipant.setMetadata(JSON.stringify(meta));
                        }
                        catch (error) {
                            console.warn('Failed to update local participant metadata after reconnect', error);
                        }
                    })();
                }
            });
            activeRoom.on(RoomEvent.Disconnected, (reason) => {
                console.debug(`Disconnected from room with reason ${reason}`);
                // [psy-wait] ВРЕМЕННАЯ ДИАГНОСТИКА: причина выпадения из комнаты.
                console.warn('[psy-wait] Disconnected', { reason, identity: activeRoom.localParticipant.identity });
                pushCallDebugLog('psy-wait', `Disconnected: ${reason}`, { reason: String(reason) }, 'warn');
                if (statsTimerRef.current) {
                    clearInterval(statsTimerRef.current);
                }
                if (localAudioTrackRef.current) {
                    localAudioTrackRef.current.stop();
                }
                if (localVideoTrackRef.current) {
                    localVideoTrackRef.current.stop();
                }
                setConnected(false);
                if (reconnectToastId.current !== null) {
                    toast.dismiss(reconnectToastId.current);
                    reconnectToastId.current = null;
                }
            });
        }
        catch (error) {
            setConnected(false);
            toast.error('При подключении к серверу произошла непредвиденная ошибка');
            console.error(`Failed to connect to room ${roomCode} for user ${userName}`, error);
            // [psy-wait] ВРЕМЕННАЯ ДИАГНОСТИКА: после этой ветки опрос состава комнаты не запускается.
            console.error('[psy-wait] ПОДКЛЮЧЕНИЕ УПАЛО', {
                requested: userName,
                roomState: activeRoom.state,
                error: String(error),
                name: error?.name,
                reason: error?.reason,
            });
            pushCallDebugLog('psy-wait', `Подключение упало через ${Date.now() - connectStartedAt} мс`, { requested: userName, error: String(error) }, 'error');
        }
    }, [roomCode, generateToken, livekitServerUrl]);
    const initDevices = useCallback(async (isCameraEnabled = true, keepLiveMicrophone = false) => {
        const { audios, videos, audioOutputs } = await getDevices();
        const { selectedAudio, selectedAudioOutput, selectedVideo } = resolvePreferredDevices({
            audios,
            videos,
            audioOutputs,
        });
        let audioTrackPromise = null;
        let videoTrackPromise = null;
        // Посреди встречи живую дорожку микрофона не трогаем: новая создаётся включённой и
        // неопубликованной, то есть выключенный микрофон «включался» сам, а включённый замолкал.
        const previousAudioTrack = localAudioTrackRef.current;
        const wasMicrophoneMuted = previousAudioTrack?.isMuted ?? true;
        const isMicrophoneKept = keepLiveMicrophone && previousAudioTrack?.mediaStreamTrack?.readyState === 'live';
        if (audios.length <= 0) {
            toast.error('Микрофон не обнаружен');
            console.warn('getUserMedia audio device empty');
        }
        else if (!isMicrophoneKept) {
            audioTrackPromise = createLocalAudioTrack({
                ...MICROPHONE_CAPTURE_OPTIONS,
                ...(selectedAudio ? { deviceId: selectedAudio.deviceId } : {}),
            }).catch((err) => {
                isMicrophoneFailed.current = true;
                console.error(`Failed to get audio input devices ${err}`);
                pushCallDebugLog('publish', 'Не удалось получить локальный аудиотрек (микрофон)', { error: String(err) }, 'error');
                toast.error('Не удалось получить доступ к микрофону');
                return null;
            });
        }
        if (videos.length <= 0) {
            toast.error('Камера не обнаружена');
            console.warn('getUserMedia video device empty');
        }
        else {
            videoTrackPromise = createLocalVideoTrack({
                ...(isGroupConsultation
                    ? {
                        resolution: VideoPresets.h720.resolution,
                        frameRate: 30,
                    }
                    : ONE_TO_ONE_VIDEO_CAPTURE),
                ...(selectedVideo ? { deviceId: selectedVideo.deviceId } : {}),
            }).catch((err) => {
                isCameraFailed.current = true;
                console.error(`Failed to get video input devices ${err}`);
                toast.error('Не удалось получить доступ к камере');
                return null;
            });
        }
        const [audioTrackRes, videoTrackRes] = await Promise.allSettled([audioTrackPromise, videoTrackPromise]);
        if (localAudioTrackRef.current && !isMicrophoneKept) {
            localAudioTrackRef.current.stop();
            localAudioTrackRef.current = null;
        }
        if (localVideoTrackRef.current) {
            localVideoTrackRef.current.stop();
            localVideoTrackRef.current = null;
            setLocalVideoTrack(null);
        }
        if (!isMicrophoneKept && audioTrackRes?.status === 'fulfilled') {
            localAudioTrackRef.current = audioTrackRes.value;
            if (keepLiveMicrophone && wasMicrophoneMuted) {
                await localAudioTrackRef.current?.mute();
            }
            setIsAudioMuted(localAudioTrackRef.current?.isMuted ?? true);
            const deviceId = localAudioTrackRef.current?.mediaStreamTrack?.getSettings()?.deviceId;
            const audioDevice = audios.find((d) => d.deviceId === deviceId);
            if (audioDevice) {
                setLocalAudio((prevAudio) => {
                    if (prevAudio?.deviceId !== audioDevice?.deviceId) {
                        return audioDevice;
                    }
                    return prevAudio;
                });
            }
            else {
                console.error(`Created track for unknown audio device ${deviceId}, stopping it`);
                localAudioTrackRef.current?.stop();
                localAudioTrackRef.current = null;
            }
        }
        if (videoTrackRes?.status === 'fulfilled') {
            localVideoTrackRef.current = videoTrackRes.value;
            setLocalVideoTrack(videoTrackRes.value);
            // Вход с выключенной камерой: гасим дорожку до публикации, иначе собеседник
            // успевает увидеть кадр, а кнопка камеры расходится с реальным состоянием.
            applyInitialVideoState(videoTrackRes.value, isCameraEnabled);
            setIsVideoMuted(!isCameraEnabled || (videoTrackRes.value?.isMuted ?? false));
            if (!isBackgroundSupported) {
                console.warn('Background processors not supported');
            }
            const deviceId = localVideoTrackRef.current?.mediaStreamTrack?.getSettings()?.deviceId;
            const videoDevice = videos.find((d) => d.deviceId === deviceId);
            if (videoDevice) {
                setLocalVideo((prevVideo) => {
                    if (prevVideo?.deviceId !== videoDevice?.deviceId) {
                        return videoDevice;
                    }
                    return prevVideo;
                });
            }
            else {
                console.error(`Created track for unknown video device ${deviceId}, stopping it`);
                localVideoTrackRef.current?.stop();
                localVideoTrackRef.current = null;
                setLocalVideoTrack(null);
            }
        }
        if (Array.isArray(audioOutputs) && audioOutputs.length > 0) {
            const newAudioOut = selectedAudioOutput ?? audioOutputs[0];
            if (newAudioOut?.deviceId) {
                setLocalAudioOutput((prevAudioOut) => {
                    if (prevAudioOut?.deviceId !== newAudioOut.deviceId) {
                        audioOutputDeviceIdRef.current = newAudioOut.deviceId;
                        return newAudioOut;
                    }
                    return prevAudioOut;
                });
            }
            else {
                console.warn('⚠️ audioOutput device has no deviceId');
                isAudioFailed.current = true;
            }
        }
        else {
            console.warn('⚠️ No audioOutput devices available');
            isAudioFailed.current = true;
        }
        // Safari не поддерживает выбор output-устройств (audiooutput), поэтому мы не считаем это ошибкой
        const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
        if (isAudioFailed.current && !isMicrophoneFailed.current) {
            if (!isSafari) {
                toast.error('Не удалось получить доступ устройствам воспроизведения звука');
            }
        }
    }, [isBackgroundSupported]);
    /** Перезахват устройств посреди встречи (окно настроек): состояние микрофона сохраняется. */
    const prepareLocalMedia = useCallback(() => initDevices(true, true), [initDevices]);
    const getPeerMetricsManager = useCallback(() => {
        if (!peerMetricsManagerRef.current) {
            peerMetricsManagerRef.current = new PeerMetricsManager(roomCode, roomRef.current, livekitServerHost);
        }
        return peerMetricsManagerRef.current;
    }, [livekitServerHost, roomCode]);
    const cleanupPeerMetrics = useCallback(async () => {
        if (peerMetricsManagerRef.current) {
            try {
                await peerMetricsManagerRef.current.cleanup();
            }
            finally {
                peerMetricsManagerRef.current = null;
            }
        }
    }, []);
    /**
     * Подключения выстраиваются в очередь: смена identity — это disconnect + connect,
     * и параллельный вызов (лобби и вход одновременно) оставил бы комнату в неопределённом состоянии.
     */
    const enqueueConnect = useCallback((userName) => {
        const result = connectQueueRef.current.catch(() => undefined).then(() => connectToRoom(userName));
        connectQueueRef.current = result.catch(() => undefined);
        return result;
    }, [connectToRoom]);
    /**
     * Привести identity текущего (ещё не вошедшего) участника к нужному.
     * Нужно групповому клиенту: он должен быть в комнате под своим постоянным identity
     * ещё в лобби, иначе адресное разрешение входа по data channel до него не доходит.
     */
    const ensureIdentity = useCallback(async (identity) => {
        // [psy-wait] ВРЕМЕННАЯ ДИАГНОСТИКА: групповой клиент меняет здесь случайный identity на постоянный.
        const skipReason = !identity
            ? 'пустой identity'
            : isJoinedRef.current
                ? 'уже вошёл'
                : roomRef.current.state !== ConnectionState.Connected
                    ? `комната в состоянии ${roomRef.current.state}`
                    : roomRef.current.localParticipant.identity === identity
                        ? 'identity уже нужный'
                        : null;
        console.info('[psy-wait] ensureIdentity', {
            identity,
            currentIdentity: roomRef.current.localParticipant.identity,
            roomState: roomRef.current.state,
            skipReason,
            willReconnect: skipReason === null,
        });
        pushCallDebugLog('psy-wait', `ensureIdentity → ${skipReason ?? 'переподключаемся'}`, {
            identity,
            currentIdentity: roomRef.current.localParticipant.identity,
        });
        if (skipReason !== null)
            return;
        await enqueueConnect(identity);
    }, [enqueueConnect]);
    const initializeConnection = useCallback(async (userName, isCameraEnabled = true) => {
        console.debug('🟡 initializeConnection: started');
        // Устройства поднимает joinRoom: в лобби камерой владеет превью, и второй
        // getUserMedia отсюда оставил бы превью чёрным.
        initialCameraEnabledRef.current = isCameraEnabled;
        await enqueueConnect(userName);
        console.debug('✅ Room connection attempted');
    }, [enqueueConnect]);
    // Предварительное подключение под случайным identity: разблокирует кнопку входа
    // (initialized) и показывает состав комнаты до входа. Настоящий identity ставится
    // позже — ensureIdentity в лобби либо initializeConnection при входе. Локальные
    // треки здесь намеренно не создаются: камерой в лобби владеет превью.
    useEffect(() => {
        // Только один раз за жизнь хука: leaveRoom подменяет Room новым инстансом,
        // и без этой отсечки выход из сессии тут же возвращал бы участника в комнату.
        if (!preconnectRequestedRef.current) {
            preconnectRequestedRef.current = true;
            void enqueueConnect(`user_${randomString(12)}_${Date.now()}`);
        }
        return () => {
            room.disconnect();
        };
    }, [enqueueConnect, room]);
    useLiveKitRtcStats({
        room,
        connected,
        roomCode,
        localAudioTrackRef,
        localVideoTrackRef,
        audioOutputDeviceIdRef,
        statsTimerRef,
        onStatsReport,
        onMicrophoneUnheardChange: setIsMicrophoneUnheard,
    });
    useEffect(() => {
        if (localAudioOutput?.deviceId) {
            isAudioFailed.current = false;
        }
        if (localAudio?.deviceId) {
            isAudioFailed.current = false;
        }
        if (localVideo?.deviceId) {
            isCameraFailed.current = false;
        }
    }, [localAudio?.deviceId, localAudioOutput?.deviceId, localVideo?.deviceId]);
    const publishLocalAudio = useCallback(async () => {
        if (localAudioTrackRef.current &&
            localAudioTrackRef.current.mediaStreamTrack?.enabled &&
            localAudioTrackRef.current.mediaStreamTrack?.readyState === 'live') {
            // публикация существующего трека
            try {
                console.debug(`Publishing local audio from existing local track ${localAudioTrackRef.current.id}`);
                await roomRef.current.localParticipant.publishTrack(localAudioTrackRef.current, {
                    source: Track.Source.Microphone,
                });
                pushCallDebugLog('publish', 'Локальный микрофон опубликован (publishTrack успешен)', {
                    trackId: localAudioTrackRef.current.id,
                });
            }
            catch (error) {
                console.error('Failed to publish local audio', error);
                pushCallDebugLog('publish', 'publishTrack(микрофон) упал', { error: String(error) }, 'error');
                toast.error('Произошла ошибка при попытке отправить аудиопоток');
                throw error;
            }
        }
        else {
            pushCallDebugLog('publish', 'publishLocalAudio вызван, но локальный трек недоступен/не live — публикация пропущена', {
                hasTrack: Boolean(localAudioTrackRef.current),
                enabled: localAudioTrackRef.current?.mediaStreamTrack?.enabled,
                readyState: localAudioTrackRef.current?.mediaStreamTrack?.readyState,
            }, 'warn');
            // Нет дорожки вовсе — об этом уже сказал тост про микрофон; замьюченная дорожка уйдёт при включении.
            // Плашка нужна, когда микрофон включён, а дорожка не опубликована.
            if (localAudioTrackRef.current && !localAudioTrackRef.current.isMuted) {
                setIsMicrophoneUnheard(true);
            }
        }
    }, []);
    const publishLocalVideo = useCallback(async () => {
        const localVideoTrack = localVideoTrackRef.current;
        // Дорожку с выключенной камерой (enabled = false) тоже публикуем: иначе
        // при входе без видео у собеседника не появляется плитка участника.
        if (localVideoTrack && localVideoTrack.mediaStreamTrack?.readyState === 'live') {
            // публикация существующего трека
            try {
                console.debug(`Publishing local video from existing local track ${localVideoTrack.id}`);
                const shouldMuteAfterPublish = localVideoTrack.mediaStreamTrack.enabled === false;
                await roomRef.current.localParticipant.publishTrack(localVideoTrack, {
                    source: Track.Source.Camera,
                });
                await finalizeInitialVideoState(localVideoTrack, shouldMuteAfterPublish);
            }
            catch (error) {
                console.error('Failed to publish local video', error);
                toast.error('Произошла ошибка при попытке отправить видеопоток');
                throw error;
            }
        }
        setLocalVideoStream({
            attachVideo: (videoRef) => {
                localVideoTrackRef.current?.attach(videoRef);
            },
            remove: (el) => {
                if (el) {
                    localVideoTrackRef.current?.detach(el);
                }
                else {
                    localVideoTrackRef.current?.detach();
                }
            },
        });
    }, []);
    /**
     * Выход из комнаты: disconnect + новый Room instance.
     * Reuse одного Room после disconnect давал нестабильный rejoin (remote tracks «терялись»).
     */
    const resolvePendingModerationAck = (key, ok) => {
        const pending = pendingModerationAcksRef.current.get(key);
        if (!pending)
            return;
        clearTimeout(pending.timer);
        pendingModerationAcksRef.current.delete(key);
        pending.resolve(ok);
    };
    const getRemoteAudioPlayback = useCallback(() => {
        if (!remoteAudioPlaybackRef.current) {
            remoteAudioPlaybackRef.current = createRemoteAudioPlayback();
        }
        return remoteAudioPlaybackRef.current;
    }, []);
    /**
     * Аудио участника подключается независимо от того, создана ли уже его видео-плитка
     * и смонтирована ли она сейчас в DOM.
     */
    const attachRemoteAudio = useCallback((identity, track) => {
        const playback = getRemoteAudioPlayback();
        // Устройство вывода могло быть выбрано до того, как участник подключился.
        if (audioOutputDeviceIdRef.current) {
            playback.setSinkId(audioOutputDeviceIdRef.current);
        }
        playback.attach(identity, track);
        pushCallDebugLog('subscribe', `Аудио ${identity} подключено к выделенному аудиоэлементу`, { identity });
    }, [getRemoteAudioPlayback]);
    const detachRemoteAudio = useCallback((identity) => {
        getRemoteAudioPlayback().detach(identity);
    }, [getRemoteAudioPlayback]);
    const syncLocalAudioMuted = useCallback((publication) => {
        if (publication.source === Track.Source.Microphone) {
            setIsAudioMuted(publication.isMuted);
        }
    }, []);
    const updateRemoteVideoMuted = useCallback((identity, isMuted) => {
        const remoteFeed = remoteStreamsMap.current.get(identity);
        if (!remoteFeed || remoteFeed.isScreenShare)
            return;
        if (remoteFeed.isVideoMuted === isMuted)
            return;
        setRemoteVideoMuted(remoteFeed, isMuted);
        setRemoteStreams((prev) => prev.map((feed) => feed.identity === identity && !feed.isScreenShare ? { ...feed, isVideoMuted: isMuted } : feed));
    }, []);
    const bindRemoteVideoState = useCallback((identity, track) => {
        remoteVideoStateCleanupRef.current.get(identity)?.();
        const cleanup = subscribeToRemoteVideoState(track, (isMuted) => updateRemoteVideoMuted(identity, isMuted));
        remoteVideoStateCleanupRef.current.set(identity, cleanup);
    }, [updateRemoteVideoMuted]);
    const reconcileRemoteParticipantCamera = useCallback((participant) => {
        const publication = participant.getTrackPublication(Track.Source.Camera);
        updateRemoteVideoMuted(participant.identity, getRemoteVideoMuted(publication));
    }, [updateRemoteVideoMuted]);
    const reconcileRemoteParticipants = useCallback(() => {
        const activeRoom = roomRef.current;
        pushCallDebugLog('reconnect', `reconcileRemoteParticipants: участников ${activeRoom.remoteParticipants.size}`);
        activeRoom.remoteParticipants.forEach((participant) => {
            participant.trackPublications.forEach((publication) => {
                if (!publication.isSubscribed) {
                    pushCallDebugLog('reconnect', `${participant.identity}: ${publication.kind} не был подписан — досасываю`, { identity: participant.identity, kind: publication.kind, source: publication.source }, 'warn');
                    publication.setSubscribed(true);
                }
            });
            const cameraPublication = participant.getTrackPublication(Track.Source.Camera);
            const remoteFeed = remoteStreamsMap.current.get(participant.identity);
            if (cameraPublication?.track && remoteFeed?.videoElement) {
                cameraPublication.track.attach(remoteFeed.videoElement);
            }
            // Аудио после реконнекта переприкрепляем так же, как и камеру: подписка могла
            // пересоздаться, и старый элемент указывал бы на мёртвый трек.
            const microphonePublication = participant.getTrackPublication(Track.Source.Microphone);
            if (microphonePublication?.track) {
                attachRemoteAudio(participant.identity, microphonePublication.track);
            }
            else {
                detachRemoteAudio(participant.identity);
            }
            reconcileRemoteParticipantCamera(participant);
        });
    }, [attachRemoteAudio, detachRemoteAudio, reconcileRemoteParticipantCamera]);
    reconcileRemoteParticipantsRef.current = reconcileRemoteParticipants;
    const leaveRoom = useCallback(async () => {
        if (leavePromiseRef.current) {
            await leavePromiseRef.current;
            return;
        }
        leavePromiseRef.current = (async () => {
            console.debug('Leaving room');
            const activeRoom = roomRef.current;
            if (remoteSyncTimerRef.current) {
                clearTimeout(remoteSyncTimerRef.current);
                remoteSyncTimerRef.current = null;
            }
            await cleanupScreenShare();
            setJoined(false);
            setIsLocalActiveSpeaker(false);
            setIsMicrophoneUnheard(false);
            activeSpeakersRef.current.update([]);
            isJoinedRef.current = false;
            setConnected(false);
            activeRoom.removeAllListeners(RoomEvent.TrackSubscribed);
            activeRoom.removeAllListeners(RoomEvent.TrackUnsubscribed);
            activeRoom.removeAllListeners(RoomEvent.TrackPublished);
            activeRoom.removeAllListeners(RoomEvent.TrackUnpublished);
            activeRoom.removeAllListeners(RoomEvent.TrackMuted);
            activeRoom.removeAllListeners(RoomEvent.TrackUnmuted);
            activeRoom.removeAllListeners(RoomEvent.AudioPlaybackStatusChanged);
            activeRoom.removeAllListeners(RoomEvent.ParticipantConnected);
            activeRoom.removeAllListeners(RoomEvent.ParticipantMetadataChanged);
            activeRoom.removeAllListeners(RoomEvent.ParticipantDisconnected);
            activeRoom.removeAllListeners(RoomEvent.ActiveSpeakersChanged);
            activeRoom.removeAllListeners(RoomEvent.Disconnected);
            activeRoom.removeAllListeners(RoomEvent.Reconnecting);
            activeRoom.removeAllListeners(RoomEvent.Reconnected);
            activeRoom.removeAllListeners(RoomEvent.LocalAudioSilenceDetected);
            activeRoom.removeAllListeners(RoomEvent.TrackSubscriptionFailed);
            activeRoom.removeAllListeners(RoomEvent.MediaDevicesError);
            activeRoom.removeAllListeners(RoomEvent.TrackStreamStateChanged);
            pendingModerationAcksRef.current.forEach((pending) => {
                clearTimeout(pending.timer);
                pending.resolve(false);
            });
            pendingModerationAcksRef.current.clear();
            if (reconnectToastId.current !== null) {
                toast.dismiss(reconnectToastId.current);
                reconnectToastId.current = null;
            }
            try {
                activeRoom.unregisterTextStreamHandler('consulation-topic');
            }
            catch {
                // handler may be absent before first join
            }
            try {
                activeRoom.unregisterByteStreamHandler(MEET_CHAT_IMAGE_TOPIC);
            }
            catch {
                // handler may be absent before first join
            }
            if (activeRoom.localParticipant.trackPublications) {
                const unpublishTasks = [];
                activeRoom.localParticipant.trackPublications.forEach((publication) => {
                    if (publication.track) {
                        unpublishTasks.push(activeRoom.localParticipant.unpublishTrack(publication.track, true));
                    }
                });
                await Promise.allSettled(unpublishTasks);
            }
            setLocalVideoStream((prev) => {
                prev?.remove?.();
                return null;
            });
            setRemoteStreams((prev) => {
                prev.forEach((vs) => vs.remove?.());
                return [];
            });
            remoteStreamsMap.current = new Map();
            remoteVideoStateCleanupRef.current.forEach((cleanup) => cleanup());
            remoteVideoStateCleanupRef.current.clear();
            getRemoteAudioPlayback().clear();
            if (activeRoom.state === ConnectionState.Connected) {
                const meta = {
                    joined: false,
                };
                try {
                    await activeRoom.localParticipant.setMetadata(JSON.stringify(meta));
                }
                catch (error) {
                    console.warn('Failed to update local metadata before leaving room', error);
                }
            }
            if (backgroundModeRef.current !== 'none') {
                try {
                    await disableBackground();
                }
                catch (error) {
                    console.warn('Failed to stop video processor before leaving room', error);
                }
            }
            try {
                await activeRoom.disconnect(true);
            }
            catch (error) {
                console.warn('Failed to disconnect room on leave', error);
            }
            localAudioTrackRef.current?.stop();
            localAudioTrackRef.current = null;
            localVideoTrackRef.current?.stop();
            localVideoTrackRef.current = null;
            setLocalVideoTrack(null);
            await cleanupPeerMetrics();
            // Fresh Room avoids stale subscription state from the previous connection.
            const nextRoom = createLiveKitRoom(isGroupConsultation);
            roomRef.current = nextRoom;
            setRoom(nextRoom);
            console.debug('Finished leaving room');
        })();
        try {
            await leavePromiseRef.current;
        }
        finally {
            leavePromiseRef.current = null;
        }
    }, [cleanupPeerMetrics, cleanupScreenShare, disableBackground, getRemoteAudioPlayback]);
    /**
     * Обработка событий подписки на треки
     * @param track
     * @param publication
     * @param participant
     * @returns
     */
    const handleTrackSubscribed = useCallback((track, publication, participant) => {
        console.debug(`Handling track ${track.kind} subscribed event from user ${participant.identity}, source: ${publication.source}`);
        const isScreenShare = publication.source === Track.Source.ScreenShare;
        const streamKey = isScreenShare ? `${participant.identity}-screenshare` : participant.identity;
        if (!shouldSubscribeToRemote(participant)) {
            console.debug(`Participant ${participant.identity} is not joinable, skipping track ${track.kind} subscription`);
            return;
        }
        const existingStream = remoteStreamsMap.current.get(streamKey);
        // Аудио подключается первым делом и не зависит ни от подписки на камеру,
        // ни от того, смонтирована ли сейчас плитка участника.
        if (track.kind === Track.Kind.Audio && !isScreenShare) {
            attachRemoteAudio(participant.identity, track);
        }
        if (isScreenShare) {
            console.log('📺 [handleTrackSubscribed] Screen share трек подписан:', {
                participant: participant.identity,
                trackId: track.mediaStreamTrack.id,
                trackState: track.mediaStreamTrack.readyState,
                streamKey,
            });
            // Удаляем старый screen share объект если он есть (на случай повторного включения)
            const oldScreenShare = remoteStreamsMap.current.get(streamKey);
            if (oldScreenShare) {
                console.log('📺 [handleTrackSubscribed] Найден старый screen share, удаляю:', {
                    display: oldScreenShare.display,
                    streamId: oldScreenShare.stream?.id,
                });
                deleteRemoteStream(streamKey);
            }
            else {
                console.log('📺 [handleTrackSubscribed] Старого screen share нет (первый раз)');
            }
            // Также удаляем из массива remoteStreams все старые screen share для этого участника
            setRemoteStreams((prevStreams) => {
                const beforeCount = prevStreams.length;
                const filtered = prevStreams.filter((stream) => {
                    const isOldScreenShare = stream.isScreenShare &&
                        (stream.display?.includes(participant.identity) ||
                            stream.display?.startsWith(participant.identity) ||
                            stream.display === `${participant.identity} (Screen Share)`);
                    if (isOldScreenShare) {
                        console.log('📺 [handleTrackSubscribed] Удаляю из массива:', stream.display);
                        // ❌ НЕ останавливаем remote треки - SDK управляет ими
                        stream.remove?.();
                    }
                    return !isOldScreenShare;
                });
                console.log('📺 [handleTrackSubscribed] Фильтрация:', beforeCount, '->', filtered.length);
                return filtered;
            });
            const screenShareStream = new MediaStream();
            screenShareStream.addTrack(track.mediaStreamTrack);
            console.log('📺 [handleTrackSubscribed] Создан новый MediaStream:', {
                streamId: screenShareStream.id,
                tracksCount: screenShareStream.getTracks().length,
            });
            const screenShareObject = {
                stream: screenShareStream,
                display: `${participant.identity} (Screen Share)`,
                isScreenShare: true,
                attachVideo: (videoRef) => {
                    console.log('📺 [attachVideo] Вызван attachVideo:', {
                        videoElement: !!videoRef,
                        oldSrcObject: !!videoRef.srcObject,
                        newStreamId: screenShareStream.id,
                    });
                    screenShareObject.videoElement = videoRef;
                    track.attach(videoRef);
                    console.log('📺 [attachVideo] srcObject установлен:', {
                        newSrcObject: !!videoRef.srcObject,
                        streamId: videoRef.srcObject?.id,
                    });
                    if (videoRef.setSinkId && audioOutputDeviceIdRef.current) {
                        console.log('📺 [attachVideo] Устанавливаю audio output:', audioOutputDeviceIdRef.current);
                        videoRef.setSinkId(audioOutputDeviceIdRef.current);
                    }
                    setTimeout(() => {
                        console.log('📺 [attachVideo] Состояние через 100ms:', {
                            srcObject: !!videoRef.srcObject,
                            readyState: videoRef.readyState,
                            paused: videoRef.paused,
                            videoWidth: videoRef.videoWidth,
                            videoHeight: videoRef.videoHeight,
                        });
                    }, 100);
                },
                remove: (videoRef) => {
                    const element = videoRef ?? screenShareObject.videoElement;
                    if (element)
                        track.detach(element);
                    screenShareObject.videoElement = undefined;
                },
            };
            console.log('📺 [handleTrackSubscribed] Создан screenShareObject:', {
                display: screenShareObject.display,
                streamId: screenShareObject.stream?.id,
            });
            addRemoteStream(screenShareObject, streamKey);
            console.log('📺 [handleTrackSubscribed] screenShareObject добавлен в remoteStreams');
            return;
        }
        if (existingStream) {
            console.debug(`Subscribing to track of kind ${publication.kind} of user ${participant.identity} to existing stream`);
            const stream = existingStream.stream;
            if (!stream) {
                console.error(`Existing feed object without stream for user ${participant.identity}`);
                return;
            }
            const trackKind = track.kind;
            const existingTracks = trackKind === Track.Kind.Audio
                ? stream.getAudioTracks()
                : trackKind === Track.Kind.Video
                    ? stream.getVideoTracks()
                    : null;
            if (existingStream) {
                console.debug(`Found existing remote track for same kind ${existingTracks?.length}`);
                existingTracks?.forEach((t) => {
                    stream.removeTrack(t);
                    // ❌ НЕ останавливаем remote треки - SDK управляет их lifecycle
                });
            }
            stream.addTrack(track.mediaStreamTrack);
            // К <video> довешиваем только картинку: звук уже играет через отдельный
            // аудиоэлемент и не зависит от того, смонтирована ли плитка.
            if (trackKind === Track.Kind.Video && existingStream.videoElement) {
                track.attach(existingStream.videoElement);
            }
            if (trackKind === Track.Kind.Audio && existingStream.identity) {
                const identity = existingStream.identity;
                existingStream.isAudioMuted = track.isMuted;
                setRemoteStreams((prev) => prev.map((s) => (s.identity === identity ? { ...s, isAudioMuted: track.isMuted } : s)));
                track.on(TrackEvent.Muted, () => {
                    setRemoteStreams((prev) => prev.map((s) => (s.identity === identity ? { ...s, isAudioMuted: true } : s)));
                });
                track.on(TrackEvent.Unmuted, () => {
                    setRemoteStreams((prev) => prev.map((s) => (s.identity === identity ? { ...s, isAudioMuted: false } : s)));
                });
            }
            if (trackKind === Track.Kind.Video) {
                bindRemoteVideoState(participant.identity, track);
            }
        }
        else {
            const cameraPublications = participant
                .getTrackPublications()
                .filter((pub) => pub.source === Track.Source.Camera ||
                pub.source === Track.Source.Microphone ||
                (!pub.source && pub.kind === Track.Kind.Audio));
            // Create feed as soon as any camera/mic track is available. Waiting for every
            // publication.isSubscribed caused missing remote video after rejoin when only
            // one track was ready synchronously after setSubscribed(true).
            const remoteStream = new MediaStream();
            cameraPublications.forEach((pub) => {
                if ((pub.kind === Track.Kind.Video || pub.kind === Track.Kind.Audio) && pub.track?.mediaStreamTrack) {
                    console.debug(`Subscribing to track of kind ${pub.kind} of user ${participant.identity}`);
                    remoteStream.addTrack(pub.track.mediaStreamTrack);
                }
            });
            if (remoteStream.getTracks()?.length) {
                const audioPub = participant.getTrackPublication(Track.Source.Microphone);
                const videoPub = participant.getTrackPublication(Track.Source.Camera);
                const initialAudioMuted = audioPub?.track?.isMuted ?? true;
                const initialVideoMuted = getRemoteVideoMuted(videoPub);
                const remoteStreamObject = {
                    stream: remoteStream,
                    identity: participant.identity,
                    display: participant.name || participant.identity,
                    isScreenShare: false,
                    isAudioMuted: initialAudioMuted,
                    isVideoMuted: initialVideoMuted,
                    attachVideo: (videoRef) => {
                        remoteStreamObject.videoElement = videoRef;
                        attachCurrentParticipantTracks(participant, videoRef);
                    },
                    remove: (videoRef) => {
                        const element = videoRef ?? remoteStreamObject.videoElement;
                        if (element) {
                            detachCurrentParticipantTracks(participant, element);
                        }
                        if (!videoRef || remoteStreamObject.videoElement === videoRef) {
                            remoteStreamObject.videoElement = undefined;
                        }
                    },
                    subscribeActiveSpeaker: (listener) => activeSpeakersRef.current.subscribe(participant.identity, listener),
                };
                if (audioPub?.track) {
                    const identity = participant.identity;
                    const updateAudioMuted = (muted) => {
                        setRemoteStreams((prev) => prev.map((s) => (s.identity === identity ? { ...s, isAudioMuted: muted } : s)));
                    };
                    audioPub.track.on(TrackEvent.Muted, () => updateAudioMuted(true));
                    audioPub.track.on(TrackEvent.Unmuted, () => updateAudioMuted(false));
                }
                console.debug(`Created RemoteFeedObject for user ${participant.identity}`);
                pushCallDebugLog('subscribe', `Плитка создана для ${participant.identity}`, {
                    identity: participant.identity,
                    hasAudio: Boolean(audioPub?.track),
                    hasVideo: Boolean(videoPub?.track),
                    initialAudioMuted,
                    initialVideoMuted,
                });
                remoteStreamsMap.current.set(streamKey, remoteStreamObject);
                addRemoteStream(remoteStreamObject);
                if (videoPub?.track) {
                    bindRemoteVideoState(participant.identity, videoPub.track);
                }
            }
        }
    }, [attachRemoteAudio, bindRemoteVideoState]);
    const handleActiveSpeakersChanged = useCallback((speakers) => {
        setIsLocalActiveSpeaker(speakers.some((participant) => participant.isLocal));
        activeSpeakersRef.current.update(speakers.filter((participant) => !participant.isLocal).map((participant) => participant.identity));
    }, []);
    const syncRemoteParticipantPublications = useCallback((participant) => {
        if (!shouldSubscribeToRemote(participant)) {
            console.debug(`skip sync for ${participant.identity}: not subscribable yet`);
            return;
        }
        participant.trackPublications.forEach((publication) => {
            if (!publication.isSubscribed) {
                publication.setSubscribed(true);
            }
            // setSubscribed may resolve synchronously with track already present — always rebuild UI feed.
            if (publication.track) {
                handleTrackSubscribed(publication.track, publication, participant);
            }
        });
    }, [handleTrackSubscribed]);
    const syncAllRemoteParticipants = useCallback((activeRoom) => {
        activeRoom.remoteParticipants.forEach((participant) => {
            syncRemoteParticipantPublications(participant);
        });
    }, [syncRemoteParticipantPublications]);
    /** Keep retrying until existing remotes appear in UI (or we are alone). */
    const ensureExistingRemotesVisible = useCallback(async (activeRoom) => {
        const sleep = (ms) => new Promise((resolve) => {
            remoteSyncTimerRef.current = setTimeout(() => {
                remoteSyncTimerRef.current = null;
                resolve();
            }, ms);
        });
        for (let attempt = 0; attempt < 20; attempt += 1) {
            if (!isJoinedRef.current) {
                return;
            }
            syncAllRemoteParticipants(activeRoom);
            const remotes = Array.from(activeRoom.remoteParticipants.values()).filter(shouldSubscribeToRemote);
            const missingFeed = remotes.some((participant) => {
                const hasMedia = participant.trackPublications.size > 0;
                if (!hasMedia) {
                    return true;
                }
                return !remoteStreamsMap.current.has(participant.identity);
            });
            if (remotes.length > 0 && !missingFeed) {
                console.debug('Remote participants synced', {
                    attempt,
                    remoteCount: remotes.length,
                    feeds: remoteStreamsMap.current.size,
                });
                return;
            }
            // remoteParticipants can be empty for a few hundred ms right after connect —
            // do not treat that as "alone in the room" too early.
            if (remotes.length === 0 && attempt >= 8) {
                console.debug('No remote participants after wait — alone in room', { attempt });
                return;
            }
            await sleep(250);
        }
        console.warn('Timed out waiting for remote participant feeds', {
            remoteCount: activeRoom.remoteParticipants.size,
            feeds: remoteStreamsMap.current.size,
        });
    }, [syncAllRemoteParticipants]);
    /**
     * Подключение к комнате (публикация своих стримов и подписка на стримы других участников)
     * @param roomName
     * @param userName
     */
    const joinRoom = useCallback(async (_roomName, userName, userId, role) => {
        console.debug('Joining room');
        if (leavePromiseRef.current) {
            await leavePromiseRef.current;
        }
        if (isJoinedRef.current || joiningRef.current)
            return;
        joiningRef.current = true;
        const activeRoom = roomRef.current;
        try {
            const peerMetricsManager = getPeerMetricsManager();
            await peerMetricsManager?.ensureSession({ userId, userName, role });
            await activeRoom.localParticipant.setName(userName);
            // [psy-wait] ВРЕМЕННАЯ ДИАГНОСТИКА: имя участника — единственный признак роли для лобби.
            console.info('[psy-wait] setName применён', {
                requested: userName,
                identity: activeRoom.localParticipant.identity,
                name: activeRoom.localParticipant.name,
            });
            pushCallDebugLog('psy-wait', `setName: ${userName}`, {
                identity: activeRoom.localParticipant.identity,
                name: activeRoom.localParticipant.name,
            });
            // Attach listeners BEFORE publish so we never miss TrackSubscribed from existing remotes.
            activeRoom.on(RoomEvent.TrackSubscribed, handleTrackSubscribed);
            activeRoom.on(RoomEvent.TrackMuted, (publication, participant) => {
                if (participant.isLocal) {
                    syncLocalAudioMuted(publication);
                }
                if (publication.source === Track.Source.Camera) {
                    updateRemoteVideoMuted(participant.identity, true);
                }
                else if (publication.source === Track.Source.Microphone) {
                    pushCallDebugLog('mute', `Микрофон ${participant.identity} выключен (mute)`, {
                        identity: participant.identity,
                    });
                }
            });
            activeRoom.on(RoomEvent.TrackUnmuted, (publication, participant) => {
                if (participant.isLocal) {
                    syncLocalAudioMuted(publication);
                }
                if (publication.source === Track.Source.Camera) {
                    updateRemoteVideoMuted(participant.identity, false);
                }
                else if (publication.source === Track.Source.Microphone) {
                    pushCallDebugLog('mute', `Микрофон ${participant.identity} включен (unmute)`, {
                        identity: participant.identity,
                    });
                }
            });
            activeRoom.on(RoomEvent.ActiveSpeakersChanged, handleActiveSpeakersChanged);
            /**
             * SDK детектирует полную тишину в опубликованном локальном аудиотреке
             * (не путать с mute) — типичный признак мёртвого/неверно выбранного
             * микрофона: трек публикуется успешно, но там физически нет сигнала.
             */
            activeRoom.on(RoomEvent.LocalAudioSilenceDetected, () => {
                pushCallDebugLog('publish', 'Локальный микрофон публикует полную тишину — возможна аппаратная проблема с устройством', undefined, 'error');
                // Замьюченная дорожка тоже «молчит» — это не поломка
                if (localAudioTrackRef.current && !localAudioTrackRef.current.isMuted) {
                    setIsMicrophoneUnheard(true);
                }
            });
            /**
             * Не удалось подписаться на уже опубликованный трек участника —
             * отличается от TrackUnsubscribed (там подписка была и её сняли).
             */
            activeRoom.on(RoomEvent.TrackSubscriptionFailed, (trackSid, participant) => {
                pushCallDebugLog('subscribe', `Не удалось подписаться на трек ${trackSid} участника ${participant.identity}`, { identity: participant.identity, trackSid }, 'error');
            });
            /**
             * Ошибка getUserMedia при (пере)создании локального трека — устройство
             * занято другим приложением, отозвано разрешение и т.п.
             */
            activeRoom.on(RoomEvent.MediaDevicesError, (error) => {
                const failure = MediaDeviceFailure.getFailure(error);
                pushCallDebugLog('publish', `Ошибка доступа к медиаустройству${failure ? ` (${failure})` : ''}: ${error.message}`, { failure, error: String(error) }, 'error');
            });
            /**
             * SFU приостанавливает подписанный трек участнику из-за нехватки
             * полосы пропускания — для аудио это ощущается как внезапно пропавший
             * звук, который сам вернётся, когда сеть позволит.
             */
            activeRoom.on(RoomEvent.TrackStreamStateChanged, (publication, streamState, participant) => {
                if (publication.source !== Track.Source.Microphone)
                    return;
                const isPaused = streamState === Track.StreamState.Paused;
                pushCallDebugLog('subscribe', `Аудио ${participant.identity}: поток ${isPaused ? 'приостановлен' : 'возобновлён'} SFU (нехватка полосы пропускания)`, { identity: participant.identity, streamState }, isPaused ? 'warn' : 'info');
            });
            /**
             * Подписка на аудио может быть снята сервером (лимиты, потеря соединения)
             * без unpublish. Отвязываем элемент, чтобы повторная подписка подключила
             * его заново, а не оставила висеть на мёртвом треке.
             */
            activeRoom.on(RoomEvent.TrackUnsubscribed, (_track, publication, participant) => {
                if (publication.source === Track.Source.Microphone) {
                    detachRemoteAudio(participant.identity);
                }
            });
            /** Браузер мог заблокировать автовоспроизведение до жеста пользователя. */
            activeRoom.on(RoomEvent.AudioPlaybackStatusChanged, () => {
                if (activeRoom.canPlaybackAudio) {
                    pushCallDebugLog('subscribe', 'Автовоспроизведение аудио разрешено браузером — возобновляем удалённое аудио');
                    getRemoteAudioPlayback().resume();
                }
                else {
                    pushCallDebugLog('subscribe', 'Браузер заблокировал автовоспроизведение аудио — звук пойдёт только после жеста пользователя', undefined, 'warn');
                }
            });
            activeRoom.on(RoomEvent.TrackPublished, (publication, participant) => {
                const isScreenShare = publication.source === Track.Source.ScreenShare;
                if (isScreenShare) {
                    console.log('📺 [TrackPublished] Screen share опубликован:', {
                        participant: participant.identity,
                        isLocal: participant.isLocal,
                        trackKind: publication.kind,
                        isSubscribed: publication.isSubscribed,
                    });
                }
                if (!shouldSubscribeToRemote(participant)) {
                    return;
                }
                console.debug(`User ${participant.identity} published track ${publication.kind}, subscribing`);
                pushCallDebugLog('publish', `TrackPublished(${publication.kind}) от ${participant.identity} → setSubscribed(true)`, { identity: participant.identity, source: publication.source, kind: publication.kind });
                publication.setSubscribed(true);
                if (publication.source === Track.Source.Camera) {
                    reconcileRemoteParticipantCamera(participant);
                }
                if (publication.track) {
                    handleTrackSubscribed(publication.track, publication, participant);
                }
                if (isScreenShare) {
                    console.log('📺 [TrackPublished] Подписка установлена');
                }
            });
            activeRoom.on(RoomEvent.ParticipantConnected, (participant) => {
                syncRemoteParticipantPublications(participant);
            });
            activeRoom.on(RoomEvent.TrackUnpublished, (publication, participant) => {
                const isScreenShare = publication.source === Track.Source.ScreenShare;
                if (isScreenShare) {
                    console.log('📺 [TrackUnpublished] Screen share отменен:', {
                        participant: participant.identity,
                        isLocal: participant.isLocal,
                        trackKind: publication.kind,
                        streamKey: `${participant.identity}-screenshare`,
                    });
                }
                console.debug(`User ${participant.identity} unpublished track ${publication.kind}, source: ${publication.source}`);
                const streamKey = isScreenShare ? `${participant.identity}-screenshare` : participant.identity;
                if (publication.source === Track.Source.Camera) {
                    remoteVideoStateCleanupRef.current.get(participant.identity)?.();
                    remoteVideoStateCleanupRef.current.delete(participant.identity);
                    updateRemoteVideoMuted(participant.identity, true);
                }
                if (publication.source === Track.Source.Microphone) {
                    detachRemoteAudio(participant.identity);
                }
                if (isScreenShare) {
                    setRemoteStreams((prevStreams) => {
                        const filtered = prevStreams.filter((stream) => {
                            const isMatchingScreenShare = stream.isScreenShare &&
                                (stream.display?.includes(participant.identity) ||
                                    stream.display?.startsWith(participant.identity) ||
                                    stream.display === `${participant.identity} (Screen Share)`);
                            if (isMatchingScreenShare) {
                                stream.remove?.();
                            }
                            return !isMatchingScreenShare;
                        });
                        return filtered;
                    });
                    remoteStreamsMap.current.delete(streamKey);
                }
                else if (publication.track?.mediaStreamTrack) {
                    const existingStream = remoteStreamsMap.current.get(streamKey);
                    if (existingStream?.stream) {
                        console.debug(`Removing track ${publication.kind} for user ${participant.identity}`);
                        existingStream.stream.removeTrack(publication.track.mediaStreamTrack);
                    }
                }
            });
            activeRoom.on(RoomEvent.ParticipantMetadataChanged, (_prevMeta, participant) => {
                if (participant.isLocal) {
                    return;
                }
                console.debug(`Changed meta of user ${participant.identity}`);
                if (shouldSubscribeToRemote(participant) || isJoined(participant)) {
                    console.debug(`User ${participant.identity} joined, subscribing to tracks`);
                    syncRemoteParticipantPublications(participant);
                    return;
                }
                console.debug(`User ${participant.identity} is hidden`);
                deleteRemoteStream(participant.identity);
                deleteRemoteStream(`${participant.identity}-screenshare`);
            });
            activeRoom.registerTextStreamHandler('consulation-topic', async (reader, participantInfo) => {
                const info = reader.info;
                console.debug(`Received text stream from ${participantInfo.identity}\n` +
                    `  Topic: ${info.topic}\n` +
                    `  Timestamp: ${info.timestamp}\n` +
                    `  ID: ${info.id}\n` +
                    `  Size: ${info.size}`);
                const text = await reader.readAll();
                console.debug(`Received event: ${text}`);
                if (text === 'destroyed') {
                    setDestroyed(true);
                    await leaveRoom();
                }
            });
            activeRoom.on(RoomEvent.ParticipantDisconnected, (participant) => {
                remoteVideoStateCleanupRef.current.get(participant.identity)?.();
                remoteVideoStateCleanupRef.current.delete(participant.identity);
                detachRemoteAudio(participant.identity);
                deleteRemoteStream(participant.identity);
                deleteRemoteStream(`${participant.identity}-screenshare`);
                // Срабатывает для любого участника, включая ещё не допущенного в комнату
                // (лобби групповой терапии) — по этому событию очередь ожидания сама
                // избавляется от тех, кто закрыл вкладку или потерял соединение.
                onRemoteParticipantLeft?.(participant.identity);
            });
            // Subscribe to people already in the room before publishing ourselves.
            syncAllRemoteParticipants(activeRoom);
            const audioLive = localAudioTrackRef.current?.mediaStreamTrack?.readyState === 'live';
            const videoLive = localVideoTrackRef.current?.mediaStreamTrack?.readyState === 'live';
            if (!audioLive || !videoLive) {
                await initDevices(initialCameraEnabledRef.current);
            }
            try {
                await Promise.all([publishLocalAudio(), publishLocalVideo()]);
            }
            catch (error) {
                console.error('Failed to publish local media', error);
                if (localAudioTrackRef.current) {
                    await activeRoom.localParticipant.unpublishTrack(localAudioTrackRef.current);
                }
                if (localVideoTrackRef.current) {
                    await activeRoom.localParticipant.unpublishTrack(localVideoTrackRef.current);
                }
                throw error;
            }
            isJoinedRef.current = true;
            setJoined(true);
            handleActiveSpeakersChanged(activeRoom.activeSpeakers);
            const meta = {
                joined: true,
            };
            try {
                await activeRoom.localParticipant.setMetadata(JSON.stringify(meta));
                // [psy-wait] ВРЕМЕННАЯ ДИАГНОСТИКА: без joined:true участник невидим для лобби.
                console.info('[psy-wait] setMetadata применён', {
                    identity: activeRoom.localParticipant.identity,
                    name: activeRoom.localParticipant.name,
                    metadata: activeRoom.localParticipant.metadata,
                    tracks: activeRoom.localParticipant.trackPublications.size,
                });
                pushCallDebugLog('psy-wait', 'setMetadata joined:true', {
                    identity: activeRoom.localParticipant.identity,
                    metadata: activeRoom.localParticipant.metadata,
                });
            }
            catch (error) {
                console.warn('Failed to update local metadata after joining room', error);
                // [psy-wait] ВРЕМЕННАЯ ДИАГНОСТИКА: сюда попадаем, если сервер отклонил обновление.
                console.error('[psy-wait] setMetadata ОТКЛОНЁН', error);
                pushCallDebugLog('psy-wait', 'setMetadata отклонён', { error: String(error) }, 'error');
            }
            await ensureExistingRemotesVisible(activeRoom);
            console.debug('Finished joining room');
        }
        catch (error) {
            console.error('Failed to join to room', error);
            toast.error('Не удалось подключиться к встрече из-за непредвиденной ошибки');
            throw error;
        }
        finally {
            joiningRef.current = false;
        }
    }, [
        detachRemoteAudio,
        ensureExistingRemotesVisible,
        getPeerMetricsManager,
        getRemoteAudioPlayback,
        handleActiveSpeakersChanged,
        handleTrackSubscribed,
        initDevices,
        leaveRoom,
        onRemoteParticipantLeft,
        publishLocalAudio,
        publishLocalVideo,
        reconcileRemoteParticipantCamera,
        syncAllRemoteParticipants,
        syncRemoteParticipantPublications,
        syncLocalAudioMuted,
        updateRemoteVideoMuted,
    ]);
    // отправить разрешение на супервизор
    const sendSupervisorRequest = useCallback(() => {
        if (!room)
            return;
        const message = { type: 'supervisor_request', from: room.localParticipant.identity };
        const encoded = new TextEncoder().encode(JSON.stringify(message));
        room.localParticipant
            .publishData(encoded, { reliable: true })
            .then(() => {
            setSupervisorSend?.(true);
        })
            .catch((err) => {
            setSupervisorSend?.(null);
        });
    }, [room, setSupervisorSend]);
    // ответ супервизор
    const sendSupervisorResponse = useCallback((accepted) => {
        if (!room)
            return;
        const message = { type: 'supervisor_response', accepted, from: room.localParticipant.identity };
        const encoded = new TextEncoder().encode(JSON.stringify(message));
        room.localParticipant.publishData(encoded, { reliable: true });
    }, [room]);
    const sendRecordingStatusUpdate = useCallback((status) => {
        if (!room)
            return;
        const message = { type: 'recording_status', status, from: room.localParticipant.identity };
        const encoded = new TextEncoder().encode(JSON.stringify(message));
        room.localParticipant.publishData(encoded, { reliable: true });
    }, [room]);
    // МАК карты — отправка колоды всем клиентам через data channel
    const sendMacCardsOpen = useCallback((data) => {
        if (!room)
            return;
        const message = {
            type: 'mac_cards_open',
            from: room.localParticipant.identity,
            name: 'metaforic_cards',
            show: data.show,
            cover: data.cover,
            color: data.color,
            result: data.result,
        };
        const encoded = new TextEncoder().encode(JSON.stringify(message));
        room.localParticipant.publishData(encoded, { reliable: true });
    }, [room]);
    // Групповая терапия — клиент отправляет запрос на подключение (психолог видит в панели «Ожидают»)
    const sendGroupConnectionRequest = useCallback((displayName) => {
        if (!room)
            return;
        const message = {
            type: 'group_connection_request',
            from: room.localParticipant.identity,
            ...(typeof displayName === 'string' && displayName !== '' && { displayName }),
        };
        const encoded = new TextEncoder().encode(JSON.stringify(message));
        room.localParticipant.publishData(encoded, { reliable: true });
    }, [room]);
    // Групповая терапия — психолог разрешает подключение участнику (отправка по data channel)
    const sendConnectionAllowed = useCallback((targetIdentity) => {
        if (!room)
            return;
        const message = {
            type: 'connection_allowed',
            for: targetIdentity,
            from: room.localParticipant.identity,
        };
        const encoded = new TextEncoder().encode(JSON.stringify(message));
        room.localParticipant.publishData(encoded, { reliable: true });
    }, [room]);
    // Групповая терапия, МАК карты — клиент отправляет свой выбор по data channel
    const sendMacSelectCards = useCallback((result) => {
        if (!room)
            return;
        const message = {
            type: 'mac_select_cards',
            from: room.localParticipant.identity,
            result,
        };
        const encoded = new TextEncoder().encode(JSON.stringify(message));
        room.localParticipant.publishData(encoded, { reliable: true });
    }, [room]);
    // Групповая терапия, МАК карты — психолог закрывает инструмент, у клиентов тоже закрывается по data channel
    const sendMacCardsClose = useCallback(() => {
        if (!room)
            return;
        const message = {
            type: 'mac_cards_close',
            from: room.localParticipant.identity,
        };
        const encoded = new TextEncoder().encode(JSON.stringify(message));
        room.localParticipant.publishData(encoded, { reliable: true });
    }, [room]);
    // Групповая терапия, Детектор эмоций — психолог открывает инструмент у всех клиентов по data channel
    const sendEmotionsOpen = useCallback(() => {
        if (!room)
            return;
        const message = {
            type: 'emotions_open',
            from: room.localParticipant.identity,
        };
        const encoded = new TextEncoder().encode(JSON.stringify(message));
        room.localParticipant.publishData(encoded, { reliable: true });
    }, [room]);
    // Групповая терапия, Детектор эмоций — психолог закрывает инструмент у клиентов по data channel
    const sendEmotionsClose = useCallback(() => {
        if (!room)
            return;
        const message = {
            type: 'emotions_close',
            from: room.localParticipant.identity,
        };
        const encoded = new TextEncoder().encode(JSON.stringify(message));
        room.localParticipant.publishData(encoded, { reliable: true });
    }, [room]);
    // Групповая терапия, Детектор эмоций — клиент отправляет свой выбор по data channel
    const sendEmotionsSelect = useCallback((result) => {
        if (!room)
            return;
        const message = {
            type: 'emotions_select',
            from: room.localParticipant.identity,
            result,
        };
        const encoded = new TextEncoder().encode(JSON.stringify(message));
        room.localParticipant.publishData(encoded, { reliable: true });
    }, [room]);
    /** Групповая терапия: психолог выключает микрофон участнику (отправка по data channel) */
    const sendRequestMuteParticipant = useCallback((targetIdentity) => {
        if (!room)
            return Promise.resolve(false);
        const message = {
            type: 'request_mute',
            from: room.localParticipant.identity,
            for: targetIdentity,
        };
        const encoded = new TextEncoder().encode(JSON.stringify(message));
        room.localParticipant.publishData(encoded, { reliable: true });
        const ackKey = `mute:${targetIdentity}`;
        resolvePendingModerationAck(ackKey, false);
        return new Promise((resolve) => {
            const timer = setTimeout(() => {
                pendingModerationAcksRef.current.delete(ackKey);
                resolve(false);
            }, MODERATION_ACK_TIMEOUT_MS);
            pendingModerationAcksRef.current.set(ackKey, { resolve, timer });
        });
    }, [room]);
    /** Групповая терапия: психолог/соорганизатор останавливает видео участнику (отправка по data channel) */
    const sendRequestStopVideoParticipant = useCallback((targetIdentity) => {
        if (!room)
            return Promise.resolve(false);
        const message = {
            type: 'request_stop_video',
            from: room.localParticipant.identity,
            for: targetIdentity,
        };
        const encoded = new TextEncoder().encode(JSON.stringify(message));
        room.localParticipant.publishData(encoded, { reliable: true });
        const ackKey = `stop_video:${targetIdentity}`;
        resolvePendingModerationAck(ackKey, false);
        return new Promise((resolve) => {
            const timer = setTimeout(() => {
                pendingModerationAcksRef.current.delete(ackKey);
                resolve(false);
            }, MODERATION_ACK_TIMEOUT_MS);
            pendingModerationAcksRef.current.set(ackKey, { resolve, timer });
        });
    }, [room]);
    /** Групповая терапия: психолог удаляет участника со встречи (отправка по data channel; клиент сам отключится) */
    const sendRemoveParticipantFromMeeting = useCallback((targetIdentity) => {
        if (!room || !targetIdentity)
            return;
        const message = {
            type: 'remove_from_meeting',
            from: room.localParticipant.identity,
            for: String(targetIdentity),
        };
        const encoded = new TextEncoder().encode(JSON.stringify(message));
        room.localParticipant.publishData(encoded, {
            reliable: true,
            destinationIdentities: [String(targetIdentity)],
        });
    }, [room]);
    /** Групповая терапия: психолог назначает участника соорганизатором (отправка по data channel) */
    const sendAssignCoOrganizer = useCallback((targetIdentity) => {
        if (!room || !targetIdentity)
            return;
        const message = {
            type: 'assign_co_organizer',
            from: room.localParticipant.identity,
            for: String(targetIdentity),
        };
        const encoded = new TextEncoder().encode(JSON.stringify(message));
        room.localParticipant.publishData(encoded, {
            reliable: true,
            destinationIdentities: [String(targetIdentity)],
        });
    }, [room]);
    // Групповая терапия, МАК карты — психолог транслирует карты активного таба на «общий экран» клиентам
    const sendMacSharedView = useCallback((participantLabel, cards) => {
        if (!room)
            return;
        const message = {
            type: 'mac_shared_view',
            from: room.localParticipant.identity,
            participantLabel,
            cards,
        };
        const encoded = new TextEncoder().encode(JSON.stringify(message));
        room.localParticipant.publishData(encoded, { reliable: true });
    }, [room]);
    // Групповая терапия, Детектор эмоций — психолог транслирует эмоции активного таба на «общий экран» клиентам
    const sendEmotionsSharedView = useCallback((participantLabel, emo) => {
        if (!room)
            return;
        const message = {
            type: 'emotions_shared_view',
            from: room.localParticipant.identity,
            participantLabel,
            emo,
        };
        const encoded = new TextEncoder().encode(JSON.stringify(message));
        room.localParticipant.publishData(encoded, { reliable: true });
    }, [room]);
    const sendSandTherapyOpen = useCallback(() => {
        if (!room)
            return;
        const message = { type: 'sand_therapy_open', from: room.localParticipant.identity };
        room.localParticipant.publishData(new TextEncoder().encode(JSON.stringify(message)), { reliable: true });
    }, [room]);
    const sendSandTherapyClose = useCallback(() => {
        if (!room)
            return;
        const message = { type: 'sand_therapy_close', from: room.localParticipant.identity };
        room.localParticipant.publishData(new TextEncoder().encode(JSON.stringify(message)), { reliable: true });
    }, [room]);
    const sendSandSelectFigures = useCallback((figures) => {
        if (!room)
            return;
        const message = { type: 'sand_therapy_select_figures', from: room.localParticipant.identity, figures };
        room.localParticipant.publishData(new TextEncoder().encode(JSON.stringify(message)), { reliable: true });
    }, [room]);
    const sendSandStartPlacement = useCallback(() => {
        if (!room)
            return;
        const message = { type: 'sand_therapy_start_placement', from: room.localParticipant.identity };
        room.localParticipant.publishData(new TextEncoder().encode(JSON.stringify(message)), { reliable: true });
    }, [room]);
    const sendSandPlaceFigure = useCallback((figure) => {
        if (!room)
            return;
        const message = { type: 'sand_therapy_place_figure', from: room.localParticipant.identity, figure };
        room.localParticipant.publishData(new TextEncoder().encode(JSON.stringify(message)), { reliable: true });
    }, [room]);
    const sendSandMoveFigure = useCallback((id, x, y) => {
        if (!room)
            return;
        const message = { type: 'sand_therapy_move_figure', from: room.localParticipant.identity, id, x, y };
        room.localParticipant.publishData(new TextEncoder().encode(JSON.stringify(message)), { reliable: true });
    }, [room]);
    const sendSandRotateFigure = useCallback((id) => {
        if (!room)
            return;
        const message = { type: 'sand_therapy_rotate_figure', from: room.localParticipant.identity, id };
        room.localParticipant.publishData(new TextEncoder().encode(JSON.stringify(message)), { reliable: true });
    }, [room]);
    const sendSandResizeFigure = useCallback((id, scale) => {
        if (!room)
            return;
        const message = { type: 'sand_therapy_resize_figure', from: room.localParticipant.identity, id, scale };
        room.localParticipant.publishData(new TextEncoder().encode(JSON.stringify(message)), { reliable: true });
    }, [room]);
    const sendSandRemoveFigure = useCallback((id) => {
        if (!room)
            return;
        const message = { type: 'sand_therapy_remove_figure', from: room.localParticipant.identity, id };
        room.localParticipant.publishData(new TextEncoder().encode(JSON.stringify(message)), { reliable: true });
    }, [room]);
    const sendSandColorChange = useCallback((color) => {
        if (!room)
            return;
        const message = { type: 'sand_therapy_color_change', from: room.localParticipant.identity, color };
        room.localParticipant.publishData(new TextEncoder().encode(JSON.stringify(message)), { reliable: true });
    }, [room]);
    const sendSandClientReady = useCallback(() => {
        if (!room)
            return;
        const message = { type: 'sand_therapy_client_ready', from: room.localParticipant.identity };
        room.localParticipant.publishData(new TextEncoder().encode(JSON.stringify(message)), { reliable: true });
    }, [room]);
    const sendColorCodeOpen = useCallback(() => {
        if (!room)
            return;
        const message = { type: 'color_code_open', from: room.localParticipant.identity };
        room.localParticipant.publishData(new TextEncoder().encode(JSON.stringify(message)), { reliable: true });
    }, [room]);
    const sendColorCodeClose = useCallback(() => {
        if (!room)
            return;
        const message = { type: 'color_code_close', from: room.localParticipant.identity };
        room.localParticipant.publishData(new TextEncoder().encode(JSON.stringify(message)), { reliable: true });
    }, [room]);
    const sendColorCodeResult = useCallback((selectedColors) => {
        if (!room)
            return;
        const message = { type: 'color_code_result', from: room.localParticipant.identity, selectedColors };
        room.localParticipant.publishData(new TextEncoder().encode(JSON.stringify(message)), { reliable: true });
    }, [room]);
    const sendWheelOpen = useCallback((payload) => {
        if (!room)
            return;
        const values = Array.isArray(payload) ? payload : payload.values;
        const sectorNames = Array.isArray(payload) ? undefined : payload.sectorNames;
        const isRealtimeVisible = Array.isArray(payload) ? undefined : payload.isRealtimeVisible;
        const isSelectionUnlocked = Array.isArray(payload) ? undefined : payload.isSelectionUnlocked;
        const message = {
            type: 'wheel_open',
            from: room.localParticipant.identity,
            values,
            ...(Array.isArray(sectorNames) ? { sectorNames } : {}),
            ...(typeof isRealtimeVisible === 'boolean' ? { isRealtimeVisible } : {}),
            ...(typeof isSelectionUnlocked === 'boolean' ? { isSelectionUnlocked } : {}),
        };
        room.localParticipant.publishData(new TextEncoder().encode(JSON.stringify(message)), { reliable: true });
    }, [room]);
    const sendWheelClose = useCallback(() => {
        if (!room)
            return;
        const message = { type: 'wheel_close', from: room.localParticipant.identity };
        room.localParticipant.publishData(new TextEncoder().encode(JSON.stringify(message)), { reliable: true });
    }, [room]);
    const sendWheelChange = useCallback((values) => {
        if (!room)
            return;
        const message = { type: 'wheel_change', from: room.localParticipant.identity, values };
        room.localParticipant.publishData(new TextEncoder().encode(JSON.stringify(message)), { reliable: true });
    }, [room]);
    const sendWheelResult = useCallback((values) => {
        if (!room)
            return;
        const message = { type: 'wheel_result', from: room.localParticipant.identity, values };
        room.localParticipant.publishData(new TextEncoder().encode(JSON.stringify(message)), { reliable: true });
    }, [room]);
    const sendDpdgOpen = useCallback((payload) => {
        if (!room)
            return;
        const message = {
            type: 'dpdg_open',
            from: room.localParticipant.identity,
            initialSync: payload?.initialSync,
        };
        room.localParticipant.publishData(new TextEncoder().encode(JSON.stringify(message)), { reliable: true });
    }, [room]);
    const sendDpdgClose = useCallback(() => {
        if (!room)
            return;
        const message = { type: 'dpdg_close', from: room.localParticipant.identity };
        room.localParticipant.publishData(new TextEncoder().encode(JSON.stringify(message)), { reliable: true });
    }, [room]);
    const sendDpdgMessage = useCallback((action, data) => {
        if (!room)
            return;
        const message = { type: 'dpdg_message', from: room.localParticipant.identity, action, data };
        room.localParticipant.publishData(new TextEncoder().encode(JSON.stringify(message)), { reliable: true });
    }, [room]);
    const sendDpdgVideoControl = useCallback((payload) => {
        if (!room)
            return;
        const { type: controlType, time } = payload;
        const message = {
            type: 'dpdg_video_control',
            from: room.localParticipant.identity,
            controlType,
            ...(typeof time === 'number' ? { time } : {}),
        };
        room.localParticipant.publishData(new TextEncoder().encode(JSON.stringify(message)), { reliable: true });
    }, [room]);
    useEffect(() => {
        if (!room) {
            console.warn('Room не инициализирован');
            return;
        }
        const handleData = (payload, participant) => {
            try {
                const decoded = new TextDecoder().decode(payload);
                // publishData в LiveKit приходит отдельными сообщениями:
                // парсим каждый пакет независимо, без накопительного буфера.
                let parsed;
                try {
                    parsed = JSON.parse(decoded);
                }
                catch (error) {
                    console.warn('Не удалось распарсить data-channel сообщение', error, decoded);
                    return;
                }
                if (parsed.type === 'supervisor_request') {
                    if (room.localParticipant.identity !== parsed.from) {
                        setSupervisorSend?.(true);
                    }
                    else {
                        toast.info('Запрос супервизора отправлен');
                    }
                }
                if (parsed.type === 'supervisor_response') {
                    if (room.localParticipant.identity !== parsed.from) {
                        onSupervisorResponse?.(parsed.accepted);
                        setSupervisorSend?.(null);
                    }
                }
                if (parsed.type === 'recording_status') {
                    if (room.localParticipant.identity !== parsed.from) {
                        onRecordingStatusUpdate?.();
                    }
                }
                handleCloudsData(parsed);
                if (parsed.type === 'mac_cards_open') {
                    if (room.localParticipant.identity !== parsed.from && parsed.result?.value) {
                        onMacCardsOpen?.({
                            show: Boolean(parsed.show),
                            cover: typeof parsed.cover === 'string' ? parsed.cover : '',
                            color: typeof parsed.color === 'string' ? parsed.color : '',
                            result: {
                                name: typeof parsed.result?.name === 'string' ? parsed.result.name : '',
                                value: Array.isArray(parsed.result.value) ? parsed.result.value : [],
                            },
                        });
                    }
                }
                if (parsed.type === 'group_connection_request') {
                    if (room.localParticipant.identity !== parsed.from) {
                        onGroupConnectionRequest?.({
                            identity: parsed.from,
                            displayName: typeof parsed.displayName === 'string' ? parsed.displayName : undefined,
                        });
                    }
                }
                if (parsed.type === 'connection_allowed') {
                    if (typeof parsed.for === 'string' && parsed.for === room.localParticipant.identity) {
                        onConnectionAllowed?.();
                    }
                }
                if (parsed.type === 'mac_select_cards') {
                    if (room.localParticipant.identity !== parsed.from && Array.isArray(parsed.result)) {
                        onClientMacSelection?.(parsed.from, parsed.result);
                    }
                }
                if (parsed.type === 'mac_cards_close') {
                    if (room.localParticipant.identity !== parsed.from) {
                        onMacCardsClose?.();
                    }
                }
                if (parsed.type === 'emotions_open') {
                    if (room.localParticipant.identity !== parsed.from) {
                        onEmotionsOpen?.();
                    }
                }
                if (parsed.type === 'emotions_select') {
                    if (room.localParticipant.identity !== parsed.from && parsed.result) {
                        const cat = Array.isArray(parsed.result.category) ? parsed.result.category : [];
                        const emo = Array.isArray(parsed.result.emo) ? parsed.result.emo : [];
                        onClientEmotionsSelection?.(parsed.from, { category: cat, emo });
                    }
                }
                if (parsed.type === 'emotions_close') {
                    if (room.localParticipant.identity !== parsed.from) {
                        onEmotionsClose?.();
                    }
                }
                if (parsed.type === 'mac_shared_view') {
                    if (room.localParticipant.identity !== parsed.from) {
                        const label = typeof parsed.participantLabel === 'string' ? parsed.participantLabel : '';
                        const cards = Array.isArray(parsed.cards) ? parsed.cards : [];
                        onMacSharedView?.(label, cards);
                    }
                }
                if (parsed.type === 'emotions_shared_view') {
                    if (room.localParticipant.identity !== parsed.from) {
                        const label = typeof parsed.participantLabel === 'string' ? parsed.participantLabel : '';
                        const emo = Array.isArray(parsed.emo) ? parsed.emo : [];
                        onEmotionsSharedView?.(label, emo);
                    }
                }
                if (parsed.type === 'color_code_open') {
                    if (room.localParticipant.identity !== parsed.from) {
                        onColorCodeOpen?.();
                    }
                }
                if (parsed.type === 'color_code_close') {
                    if (room.localParticipant.identity !== parsed.from) {
                        onColorCodeClose?.();
                    }
                }
                if (parsed.type === 'color_code_result') {
                    if (room.localParticipant.identity !== parsed.from && Array.isArray(parsed.selectedColors)) {
                        onColorCodeResult?.({
                            from: String(parsed.from),
                            selectedColors: parsed.selectedColors,
                        });
                    }
                }
                if (parsed.type === 'wheel_open') {
                    if (room.localParticipant.identity !== parsed.from && Array.isArray(parsed.values)) {
                        onWheelOpen?.({
                            values: parsed.values,
                            ...(Array.isArray(parsed.sectorNames)
                                ? { sectorNames: parsed.sectorNames.filter((name) => typeof name === 'string') }
                                : {}),
                            ...(typeof parsed.isRealtimeVisible === 'boolean' ? { isRealtimeVisible: parsed.isRealtimeVisible } : {}),
                            ...(typeof parsed.isSelectionUnlocked === 'boolean'
                                ? { isSelectionUnlocked: parsed.isSelectionUnlocked }
                                : {}),
                        });
                    }
                }
                if (parsed.type === 'wheel_change') {
                    if (room.localParticipant.identity !== parsed.from && Array.isArray(parsed.values)) {
                        onWheelChange?.({
                            from: String(parsed.from),
                            values: parsed.values,
                        });
                    }
                }
                if (parsed.type === 'wheel_result') {
                    if (room.localParticipant.identity !== parsed.from && Array.isArray(parsed.values)) {
                        onWheelResult?.({
                            from: String(parsed.from),
                            values: parsed.values,
                        });
                    }
                }
                if (parsed.type === 'wheel_close') {
                    if (room.localParticipant.identity !== parsed.from) {
                        onWheelClose?.();
                    }
                }
                if (parsed.type === 'dpdg_open') {
                    if (room.localParticipant.identity !== parsed.from) {
                        onDpdgOpen?.({
                            initialSync: parsed.initialSync && typeof parsed.initialSync === 'object'
                                ? parsed.initialSync
                                : undefined,
                        });
                    }
                }
                if (parsed.type === 'dpdg_close') {
                    if (room.localParticipant.identity !== parsed.from) {
                        onDpdgClose?.();
                    }
                }
                if (parsed.type === 'dpdg_message') {
                    if (room.localParticipant.identity !== parsed.from && typeof parsed.action === 'string') {
                        onDpdgMessage?.(parsed.action, parsed.data && typeof parsed.data === 'object' ? parsed.data : {});
                    }
                }
                if (parsed.type === 'dpdg_video_control') {
                    if (room.localParticipant.identity !== parsed.from && typeof parsed.controlType === 'string') {
                        onDpdgVideoControl?.({
                            type: parsed.controlType,
                            ...(typeof parsed.time === 'number' ? { time: parsed.time } : {}),
                        });
                    }
                }
                if (parsed.type === 'sand_therapy_open') {
                    if (room.localParticipant.identity !== parsed.from) {
                        onSandTherapyOpen?.();
                    }
                }
                if (parsed.type === 'sand_therapy_close') {
                    if (room.localParticipant.identity !== parsed.from) {
                        onSandTherapyClose?.();
                    }
                }
                if (parsed.type === 'sand_therapy_select_figures') {
                    if (room.localParticipant.identity !== parsed.from && Array.isArray(parsed.figures)) {
                        onSandSelectFigures?.(parsed.figures);
                    }
                }
                if (parsed.type === 'sand_therapy_start_placement') {
                    if (room.localParticipant.identity !== parsed.from) {
                        onSandStartPlacement?.();
                    }
                }
                if (parsed.type === 'sand_therapy_place_figure') {
                    if (room.localParticipant.identity !== parsed.from && parsed.figure) {
                        onSandPlaceFigure?.(parsed.figure);
                    }
                }
                if (parsed.type === 'sand_therapy_move_figure') {
                    if (room.localParticipant.identity !== parsed.from && typeof parsed.id === 'string') {
                        onSandMoveFigure?.(parsed.id, parsed.x, parsed.y);
                    }
                }
                if (parsed.type === 'sand_therapy_rotate_figure') {
                    if (room.localParticipant.identity !== parsed.from && typeof parsed.id === 'string') {
                        onSandRotateFigure?.(parsed.id);
                    }
                }
                if (parsed.type === 'sand_therapy_resize_figure') {
                    if (room.localParticipant.identity !== parsed.from &&
                        typeof parsed.id === 'string' &&
                        typeof parsed.scale === 'number') {
                        onSandResizeFigure?.(parsed.id, parsed.scale);
                    }
                }
                if (parsed.type === 'sand_therapy_remove_figure') {
                    if (room.localParticipant.identity !== parsed.from && typeof parsed.id === 'string') {
                        onSandRemoveFigure?.(parsed.id);
                    }
                }
                if (parsed.type === 'sand_therapy_color_change') {
                    if (room.localParticipant.identity !== parsed.from && typeof parsed.color === 'string') {
                        onSandColorChange?.(parsed.color);
                    }
                }
                if (parsed.type === 'sand_therapy_client_ready') {
                    if (room.localParticipant.identity !== parsed.from) {
                        onSandClientReady?.();
                    }
                }
                const chatResult = handleChatData(parsed, participant);
                parsed = chatResult.parsed;
                if (chatResult.stopPropagation) {
                    return;
                }
                if (parsed.type === 'request_mute') {
                    if (typeof parsed.for === 'string' && parsed.for === room.localParticipant.identity) {
                        room.localParticipant
                            .setMicrophoneEnabled(false)
                            .then(() => {
                            // Ack отправляем только после реального mute — если он не пройдёт,
                            // психолог по таймауту увидит, что команда не подтвердилась.
                            const ack = { type: 'mute_ack', from: room.localParticipant.identity };
                            room.localParticipant.publishData(new TextEncoder().encode(JSON.stringify(ack)), { reliable: true });
                        })
                            .catch((err) => {
                            console.error('Failed to mute microphone by host request', err);
                        });
                        onMutedByHost?.();
                    }
                }
                if (parsed.type === 'request_stop_video') {
                    if (typeof parsed.for === 'string' && parsed.for === room.localParticipant.identity) {
                        room.localParticipant
                            .setCameraEnabled(false)
                            .then(() => {
                            const ack = { type: 'stop_video_ack', from: room.localParticipant.identity };
                            room.localParticipant.publishData(new TextEncoder().encode(JSON.stringify(ack)), { reliable: true });
                        })
                            .catch((err) => {
                            console.error('Failed to stop video by host request', err);
                        });
                        onVideoStoppedByHost?.();
                    }
                }
                if (parsed.type === 'mute_ack' && typeof parsed.from === 'string') {
                    resolvePendingModerationAck(`mute:${parsed.from}`, true);
                }
                if (parsed.type === 'stop_video_ack' && typeof parsed.from === 'string') {
                    resolvePendingModerationAck(`stop_video:${parsed.from}`, true);
                }
                if (parsed.type === 'remove_from_meeting') {
                    const targetFor = parsed.for != null ? String(parsed.for) : '';
                    const myIdentity = room.localParticipant.identity ?? '';
                    if (targetFor && targetFor === myIdentity) {
                        onRemovedFromMeeting?.();
                        room.disconnect(true);
                    }
                }
                if (parsed.type === 'assign_co_organizer') {
                    const targetFor = parsed.for != null ? String(parsed.for) : '';
                    const myIdentity = room.localParticipant.identity ?? '';
                    if (targetFor && targetFor === myIdentity) {
                        onCoOrganizerAssigned?.();
                    }
                }
                if (parsed && typeof parsed.type === 'string') {
                    notify(parsed);
                }
            }
            catch (err) {
                console.error('Критическая ошибка в DataReceived:', err);
            }
        };
        room.on(RoomEvent.DataReceived, handleData);
        return () => {
            room.off(RoomEvent.DataReceived, handleData);
        };
    }, [
        onRecordingStatusUpdate,
        onSupervisorResponse,
        room,
        setSupervisorSend,
        handleCloudsData,
        onMacCardsOpen,
        onClientMacSelection,
        onMacCardsClose,
        onEmotionsOpen,
        onClientEmotionsSelection,
        onEmotionsClose,
        onGroupConnectionRequest,
        onConnectionAllowed,
        onMutedByHost,
        onVideoStoppedByHost,
        onRemovedFromMeeting,
        onCoOrganizerAssigned,
        onMacSharedView,
        onEmotionsSharedView,
        onSandTherapyOpen,
        onSandTherapyClose,
        onSandPlaceFigure,
        onSandMoveFigure,
        onSandRotateFigure,
        onSandResizeFigure,
        onSandRemoveFigure,
        onSandColorChange,
        onSandClientReady,
        onSandSelectFigures,
        onSandStartPlacement,
        onColorCodeOpen,
        onColorCodeClose,
        onColorCodeResult,
        onWheelOpen,
        onWheelChange,
        onWheelResult,
        onWheelClose,
        onDpdgOpen,
        onDpdgClose,
        onDpdgMessage,
        onDpdgVideoControl,
        notify,
        handleChatData,
    ]);
    useEffect(() => {
        return () => {
            void cleanupPeerMetrics();
        };
    }, [cleanupPeerMetrics]);
    const addRemoteStream = (remoteStreamObject, customKey) => {
        // Ключ должен быть уникальным: customKey (screen share), иначе identity (уникален у LiveKit), иначе display
        const mapKey = customKey ?? remoteStreamObject.identity ?? remoteStreamObject.display;
        remoteStreamsMap.current.set(mapKey, remoteStreamObject);
        setRemoteStreams((remoteStreams) => {
            // Удаляем предыдущий поток того же участника по identity, чтобы не схлопывать двух людей с одинаковым именем
            const newRV = remoteStreams.filter((y) => remoteStreamObject.identity && y.identity === remoteStreamObject.identity ? false : true);
            return [...newRV, remoteStreamObject];
        });
    };
    const deleteRemoteStream = (streamKey) => {
        setRemoteStreams((prevStreams) => {
            let existingStream = null;
            const updatedStreams = [];
            for (const s of prevStreams) {
                let isMatch = false;
                if (streamKey.endsWith('-screenshare')) {
                    const baseIdentity = streamKey.replace('-screenshare', '');
                    isMatch = !!(s.isScreenShare &&
                        (s.display === `${baseIdentity} (Screen Share)` || s.display?.startsWith(baseIdentity)));
                }
                else {
                    // Удаление по identity (ParticipantDisconnected) или по display; в map поток добавлен по display
                    isMatch = !s.isScreenShare && (s.display === streamKey || s.identity === streamKey);
                }
                if (isMatch) {
                    existingStream = s;
                }
                else {
                    updatedStreams.push(s);
                }
            }
            if (existingStream) {
                // Плитка лежит в map по identity: удаление по display теряло запись,
                // и участник с тем же именем больше не появлялся.
                const keyUsedInMap = existingStream.isScreenShare ? streamKey : (existingStream.identity ?? streamKey);
                remoteStreamsMap.current.delete(keyUsedInMap);
                if (existingStream.identity && !existingStream.isScreenShare) {
                    remoteVideoStateCleanupRef.current.get(existingStream.identity)?.();
                    remoteVideoStateCleanupRef.current.delete(existingStream.identity);
                }
                console.debug(`Deleting remote stream for key ${streamKey}, display: ${existingStream.display}`);
                // ❗ НЕ останавливаем remote треки - LiveKit SDK управляет их lifecycle
                existingStream.remove?.();
                return updatedStreams;
            }
            else {
                remoteStreamsMap.current.delete(streamKey);
                console.warn(`Stream with key ${streamKey} not found in remoteStreams. Available streams:`, prevStreams.map((s) => ({ display: s.display, identity: s.identity, isScreenShare: s.isScreenShare })));
                return prevStreams;
            }
        });
    };
    /**
     * Операции с микрофоном идут по очереди (как у камеры), а состояние кнопки берётся
     * из самой дорожки после операции: раньше оно выставлялось заранее и расходилось с реальностью.
     */
    const runMicrophoneOperation = useCallback((operation, errorMessage) => {
        return enqueueMediaToggle(audioToggleQueueRef, async () => {
            try {
                await operation();
            }
            catch (error) {
                console.error('Microphone operation failed', error);
                pushCallDebugLog('publish', errorMessage, { error: String(error) }, 'error');
                toast.error(errorMessage);
            }
            const isMuted = localAudioTrackRef.current?.isMuted ?? true;
            setIsAudioMuted(isMuted);
            return isMuted;
        });
    }, []);
    const toggleAudio = useCallback(() => {
        const isMuting = Boolean(localAudioTrackRef.current && !localAudioTrackRef.current.isMuted);
        return runMicrophoneOperation(async () => {
            if (!joined)
                return;
            const track = localAudioTrackRef.current;
            if (track && !track.isMuted) {
                await track.mute();
                return;
            }
            // Ссылку снимаем до операции: если микрофон получить не удастся, старой дорожки уже нет
            localAudioTrackRef.current = null;
            localAudioTrackRef.current = await enableMicrophone(room, track, localAudio?.deviceId);
            isMicrophoneFailed.current = false;
        }, isMuting ? 'Не удалось отключить микрофон' : 'Не удалось включить микрофон');
    }, [joined, localAudio, room, runMicrophoneOperation]);
    /** Пересоздать и переопубликовать дорожку микрофона — то же, что перезаход, но без выхода из встречи. */
    const restartMicrophone = useCallback(() => {
        return runMicrophoneOperation(async () => {
            if (!joined)
                return;
            const previous = localAudioTrackRef.current;
            localAudioTrackRef.current = null;
            localAudioTrackRef.current = await republishMicrophone(room, previous, localAudio?.deviceId);
            isMicrophoneFailed.current = false;
            setIsMicrophoneUnheard(false);
            pushCallDebugLog('publish', 'Микрофон перезапущен по запросу участника', {
                trackId: localAudioTrackRef.current.id,
            });
            toast.success('Микрофон перезапущен');
        }, 'Не удалось перезапустить микрофон');
    }, [joined, localAudio, room, runMicrophoneOperation]);
    const createDeviceTrack = useCallback(async (deviceId, kind) => {
        try {
            const newDeviceStream = await navigator.mediaDevices.getUserMedia({
                audio: kind === 'audio'
                    ? {
                        deviceId,
                        autoGainControl: true,
                        channelCount: 1,
                        echoCancellation: true,
                        noiseSuppression: true,
                    }
                    : false,
                video: kind === 'video'
                    ? {
                        deviceId,
                        width: { ideal: isGroupConsultation ? VideoPresets.h720.width : VideoPresets.h540.width },
                        height: { ideal: isGroupConsultation ? VideoPresets.h720.height : VideoPresets.h540.height },
                        frameRate: { ideal: isGroupConsultation ? 30 : 24, max: isGroupConsultation ? 30 : 24 },
                    }
                    : false,
            });
            const newMediaStreamTrack = newDeviceStream.getTracks()?.find((t) => t.kind === kind);
            if (!newMediaStreamTrack) {
                console.error(`Empty ${kind} track for device ${deviceId}`);
                return;
            }
            // остановка других трэков устройства
            newDeviceStream.getTracks().forEach((t) => {
                if (t !== newMediaStreamTrack) {
                    t.stop();
                }
            });
            return newMediaStreamTrack;
        }
        catch (error) {
            console.error(`Failed to create ${kind} track for device ${deviceId}`, error);
            return;
        }
    }, [isGroupConsultation]);
    /**
     * Переключения камеры сериализуются: быстрый двойной клик успевал запустить
     * replaceTrack и mute одновременно, и трек оставался в состоянии, обратном кнопке.
     */
    const toggleVideo = useCallback(() => {
        const toggleOperation = enqueueMediaToggle(videoToggleQueueRef, async () => {
            if (!joined)
                return false;
            const localVideoTrack = localVideoTrackRef.current;
            if (!localVideoTrack) {
                throw new Error('Local video track is not available');
            }
            console.log(`${localVideoTrack.isMuted ? 'Unmuting' : 'Muting'} video for device ${localVideo?.deviceId}`);
            const isMuted = await toggleLocalVideoTrack({
                track: localVideoTrack,
                deviceId: localVideo?.deviceId,
                createDeviceTrack: (deviceId) => createDeviceTrack(deviceId, 'video'),
            });
            setIsVideoMuted(isMuted);
            return isMuted;
        });
        return toggleOperation.catch((error) => {
            console.error('Failed to toggle video track', error);
            toast.error('Не удалось переключить камеру');
            throw error;
        });
    }, [createDeviceTrack, joined, localVideo]);
    const switchMicrophone = useCallback(async (device, isMuted) => {
        if (!device)
            return;
        console.debug(`Switching microphone to device ${device.deviceId}, isMuted ${isMuted}`);
        if (localAudioTrackRef.current &&
            (localAudio?.deviceId !== device.deviceId ||
                (localAudio?.deviceId === device.deviceId &&
                    localAudioTrackRef.current?.mediaStreamTrack?.readyState !== 'live'))) {
            console.debug(`Restarting audio track for ${device.deviceId} in switchMicrophone`);
            await localAudioTrackRef.current.restartTrack({
                ...MICROPHONE_CAPTURE_OPTIONS,
                deviceId: device.deviceId,
            });
            console.debug(`Audio track restarted for ${device.deviceId} in switchMicrophone`);
            if (!isMuted && localAudioTrackRef.current.isMuted) {
                await localAudioTrackRef.current.unmute();
            }
        }
        setLocalAudio((prevAudio) => {
            return prevAudio?.deviceId !== device?.deviceId ? device : prevAudio;
        });
    }, [localAudio]);
    const switchCamera = useCallback(async (device, isMuted) => {
        if (!device)
            return;
        console.debug(`Switching camera to device ${device.deviceId}, isMuted ${isMuted}`);
        if (localVideoTrackRef.current &&
            (localVideo?.deviceId !== device.deviceId ||
                (localVideo?.deviceId === device.deviceId &&
                    localVideoTrackRef.current?.mediaStreamTrack?.readyState !== 'live'))) {
            console.debug(`Restarting video track for ${device.deviceId} in switchCamera`);
            try {
                await switchLocalVideoDevice(localVideoTrackRef.current, device.deviceId, isMuted, {
                    ...(isGroupConsultation
                        ? {
                            resolution: VideoPresets.h720.resolution,
                            frameRate: 30,
                        }
                        : ONE_TO_ONE_VIDEO_CAPTURE),
                });
                console.debug(`Video track restarted for ${device.deviceId} in switchCamera`);
            }
            catch (error) {
                console.error('Failed to switch camera', error);
            }
        }
        setLocalVideo((prevVideo) => {
            console.debug(`Setting camera to new device id ${device?.deviceId}`);
            return prevVideo?.deviceId !== device?.deviceId ? device : prevVideo;
        });
    }, [isGroupConsultation, localVideo]);
    const switchAudioOutput = useCallback((device) => {
        if (!device || localAudioOutput?.deviceId === device?.deviceId)
            return;
        console.debug(`Switching audio output ${device.deviceId}`);
        getRemoteAudioPlayback().setSinkId(device.deviceId);
        remoteStreamsMap.current?.forEach((value, _key) => {
            value.onAudioOutputChange?.(device.deviceId);
        });
        setLocalAudioOutput((prevAudioOutput) => {
            if (prevAudioOutput?.deviceId !== device?.deviceId) {
                audioOutputDeviceIdRef.current = device?.deviceId;
                return device;
            }
            return prevAudioOutput;
        });
    }, [getRemoteAudioPlayback, localAudioOutput]);
    // Аудиоэлементы живут вне React-дерева, поэтому их нужно снять явно,
    // даже если размонтирование произошло без штатного leaveRoom.
    useEffect(() => {
        return () => {
            remoteAudioPlaybackRef.current?.clear();
        };
    }, []);
    const destroyRoom = useCallback(async () => {
        const info = await room.localParticipant.sendText('destroyed', {
            topic: 'consulation-topic',
        });
        console.debug(`Sent destroyed event with stream ID: ${info.id}`);
        setDestroyed(true);
        leaveRoom();
    }, [leaveRoom, room]);
    const stopVideoTrack = useCallback(() => {
        if (localVideoTrackRef.current) {
            localVideoTrackRef.current.mute();
            localVideoTrackRef.current.stop();
        }
    }, []);
    const getRoomId = useCallback(async (_roomName) => await Promise.resolve(room.getSid()), [room]);
    // [psy-wait] ВРЕМЕННАЯ ДИАГНОСТИКА: подпись прошлого снимка, чтобы не спамить каждую секунду.
    const roomInfoProbeRef = useRef('');
    const roomInfoHeartbeatRef = useRef(0);
    const getRoomInfo = useCallback(async (_roomId) => {
        const users = [];
        room?.remoteParticipants?.forEach((participant, _key) => {
            if (isJoined(participant)) {
                users.push({
                    display: participant.name,
                });
            }
        });
        if (room.localParticipant && isJoined(room.localParticipant)) {
            users.push({
                display: room.localParticipant.name,
            });
        }
        // [psy-wait] ВРЕМЕННАЯ ДИАГНОСТИКА: полный состав комнаты и вердикт isJoined по каждому.
        const probe = {
            roomState: room.state,
            localIdentity: room.localParticipant?.identity,
            local: describeParticipantForProbe(room.localParticipant),
            remotes: Array.from(room.remoteParticipants?.values() ?? []).map(describeParticipantForProbe),
            usersDisplays: users.map((user) => user.display),
        };
        const probeSignature = JSON.stringify(probe);
        const now = Date.now();
        if (probeSignature !== roomInfoProbeRef.current) {
            roomInfoProbeRef.current = probeSignature;
            roomInfoHeartbeatRef.current = now;
            console.info('[psy-wait] состав комнаты', probe);
            pushCallDebugLog('psy-wait', 'Состав комнаты изменился', probe);
        }
        else if (now - roomInfoHeartbeatRef.current >= 15000) {
            // Тишина в логе двусмысленна: состав не менялся или опрос вообще встал.
            roomInfoHeartbeatRef.current = now;
            pushCallDebugLog('psy-wait', 'Опрос жив, состав без изменений', {
                roomState: probe.roomState,
                remotes: probe.remotes.length,
            });
        }
        return await Promise.resolve(users);
    }, [room]);
    return {
        initialized: connected,
        initializeConnection,
        prepareLocalMedia,
        joined,
        getRoomId: getRoomId,
        getRoomInfo: getRoomInfo,
        ensureIdentity,
        joinRoom: joinRoom,
        leaveRoom: leaveRoom,
        audioDevice: localAudio,
        videoDevice: localVideo,
        isAudioMuted,
        isVideoMuted,
        audioOutDevice: localAudioOutput,
        toggleAudio: toggleAudio,
        toggleVideo: toggleVideo,
        changeAudioOutput: switchAudioOutput,
        changeAudioDevice: switchMicrophone,
        changeVideoDevice: switchCamera,
        localVideo: localVideoStream,
        remoteVideos: remoteStreams,
        callDestroyRoom: destroyRoom,
        destroyed,
        stopVideoTrack: stopVideoTrack,
        room,
        isLocalActiveSpeaker,
        isMicrophoneUnheard,
        restartMicrophone,
        localVideoTrack,
        toggleBackground,
        backgroundMode,
        isBackgroundSupported,
        sendSupervisorRequest,
        sendSupervisorResponse,
        sendRecordingStatusUpdate,
        sendChatMessage,
        sendCloudsOpen,
        sendCloudsClose,
        sendCloudsDrawLine,
        sendCloudsClearDrawing,
        sendCloudsSelectImage,
        sendCloudsClientFinished,
        sendCloudsUndo,
        sendCloudsHideVideoForClient,
        sendMacCardsOpen,
        sendMacSelectCards,
        sendMacCardsClose,
        sendEmotionsOpen,
        sendEmotionsClose,
        sendEmotionsSelect,
        sendRequestMuteParticipant,
        sendRequestStopVideoParticipant,
        sendRemoveParticipantFromMeeting,
        sendAssignCoOrganizer,
        sendGroupConnectionRequest,
        sendConnectionAllowed,
        sendMacSharedView,
        sendEmotionsSharedView,
        sendColorCodeOpen,
        sendColorCodeClose,
        sendColorCodeResult,
        sendWheelOpen,
        sendWheelClose,
        sendWheelChange,
        sendWheelResult,
        sendDpdgOpen,
        sendDpdgClose,
        sendDpdgMessage,
        sendDpdgVideoControl,
        sendSandTherapyOpen,
        sendSandTherapyClose,
        sendSandSelectFigures,
        sendSandStartPlacement,
        sendSandPlaceFigure,
        sendSandMoveFigure,
        sendSandRotateFigure,
        sendSandResizeFigure,
        sendSandRemoveFigure,
        sendSandColorChange,
        sendSandClientReady,
        dataTransport,
        startScreenShare,
        stopScreenShare,
        isScreenSharing,
        connectionQuality,
    };
}
